/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {batch} from 'react-redux';
import {ColumnState} from 'ag-grid-community';
import {cloneDeep} from 'lodash';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import isUndefined from 'lodash.isundefined';
import sortby from 'lodash.sortby';
import unionBy from 'lodash.unionby';
import moment from 'moment';
import {clearQueryParams, SET_QUERY_PARAMS} from '@ion/applications/src/query/state';
import {dispatchGridColumnsChange} from '@ion/applications/src/store/helpers';
import {AccountHashMapKeyType, findCurrentAccountMatch} from '@ion/core/src/entities/Accounts';
import {getLatestTime, isIgnitionOff} from '@ion/core/src/entities/Asset';
import {get} from '@ion/core/src/environment/environment';
import {error} from '@ion/core/src/logger/logger';
import {isDeviceAlert} from '@ion/core/src/services/AlertUtils';
import {
  createGeoFence,
  deleteGeoFence,
  findNearby as findNearest,
  getAssetCount,
  getAssetHistory,
  getAssetTypesAndCategories as getTypesAndCategories,
  getCargoCount,
  getCVSAssets,
  getCVSAssetsAndCount,
  getCVSClusters,
  getCVSPaginatedAssets as loadCVSAssets,
  getSCDevices,
  loadLandmarkGeoFences,
  searchGeoFences
} from '@ion/core/src/services/ArcGIS';
import {
  CVSQueryArgs,
  FindNearbyRequestArgs,
  isScDevice,
  isSCIDevice,
  parseCollection
} from '@ion/core/src/services/ArcGISUtils';
import {
  getAsset,
  getAssociatedAssets,
  getDevice,
  getDeviceReport,
  getiOnVisionDetails,
  loadDevicesBySmartSearch,
  loadIOProfiles
} from '@ion/core/src/services/Connect';
import {
  entityId,
  entityIds
} from '@ion/core/src/services/ConnectUtils';
import Devices from '@ion/core/src/services/Device';
import {EventCode, getDefaultEventList} from '@ion/core/src/services/EventCodes';
import globalSearch, {
  GlobalSearchResult,
  revalidate
} from '@ion/core/src/services/GlobalSearch';
import {ALIGNED_ROUTES_CONFIG, massRouteMatching} from '@ion/core/src/services/Here';
import {
  createAlignedNodes,
  createCSV,
  createPathArray
} from '@ion/core/src/services/HereUtils';
import {getSensorData, loadCrashboxxData} from '@ion/core/src/services/MicroServices';
import {
  Asset,
  AssetType,
  AugmentedSmarterTrailerSensorData,
  BaseAsset,
  CrashboxxSeverityCode,
  CVSAssetType,
  Device,
  DeviceFeatureSensorTypes,
  EntityStatus,
  FindNearbyType,
  GenericObject,
  GeoFenceActions,
  GeoFenceAttributes,
  GeoFenceStatus,
  HVSRecord,
  HVSRecords,
  SensorTypes,
  TagReportType,
  ToolAssetType
} from '@ion/core/src/types/entities';
import {AssetAndCountType, SearchType} from '@ion/core/src/types/services';
import {mToKm, mToMi} from '@ion/core/src/units/conversions';
import {getUserUnit, isImperial} from '@ion/core/src/units/helpers';
import {
  endOfDay,
  getDateFromToday,
  startOfDay
} from '@ion/core/src/utils/DateUtil';
import {
  isDeepEqual
} from '@ion/core/src/utils/Equality';
import {
  safeJSONParse
} from '@ion/core/src/utils/JSONUtils';
import {
  splitBySpecialCharacters
} from '@ion/core/src/utils/StringUtil';
import {
  isBottomSheetTableView,
  isSheetTableView,
  isSideSheetTableView
} from '@ion/dls/src/components/sidesheet/helpers';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {
  getDistanceByCoordinates
} from '@ion/dls/src/maps/helpers/DistanceUtils';
import {
  augmentOperationalLayers,
  createAssetsMap,
  getAssetsAggregationsData,
  getHistoryColors,
  getInputEventColors,
  getInputMixedSelection,
  getIsLargeFleetMode,
  isRouteMatchingEnabled,
  isTrackingFilterActive
} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {CURRENT_LAYER} from '@ion/dls/src/maps/layers/CurrentLayerSettings';
import {
  HISTORY_LAYER,
  MAX_INPUT_SELECTED
} from '@ion/dls/src/maps/layers/TripHistoryLayerSettings';
import {setDefaultBasemap} from '@ion/dls/src/maps/MapPreferences';
import {HistoryDefaultInputsEnum} from '@ion/dls/src/maps/types/types';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {Notification} from '@ion/dls/src/types/extensions';
import {
  AllFilterTypes,
  AssetsMap, BaseMapEnum,
  DateSelectorValues,
  HistoryInputEvents,
  HistoryInputEventsEnum,
  HistoryReportTypeEnum,
  LabelContentType,
  SavedMapViewType,
  SideSheetView,
  TrackingFilters,
  TrackingMode
} from '@ion/dls/src/types/types';
import {augmentSensorData} from '@ion/features/src/smarterTrailer/SmarterTrailerUtils';
import QueryKey, {createKey} from '@ion/query/src/config/QueryKeys';
import {cacheOnFetch} from '@ion/query/src/utils/helpers';
import {
  filterModelConverter,
  filterModelConverterV2,
  formatSearchTerm,
  getAdditionalFilterParams,
  getAssetById,
  getAssetId,
  getDbWriteTimeFilter,
  getItemValue,
  isLiveTracking
} from '@/routes/tracking/trackingUtils';
import {ReportsMode} from '@/types/redux';
import changePage, {getRoute, Route, setQuery} from '../../routes/RouteDefinitions';
import Features, {hasCrashboxx, hasIOnVision} from '../../security/Features';
import {
  getAllAccountIds,
  getCheckedAccountIds,
  getDevicesByESN,
  getFilteredAssets,
  getFilterRequestParams,
  getGeofenceAccountIds,
  getSelectedGroupHierarchy,
  isCurrentPage,
  parseSciCollection
} from '../../store/helpers';
import {getAccountId} from '../helpers';
import {AssetSelectionType} from '../reducers/tracking';
import {
  ADD_ADMIN_GEOFENCES_ERROR,
  ADD_ADMIN_GEOFENCES_REQUEST,
  ADD_ADMIN_GEOFENCES_RESPONSE
} from './admin-geofences';
import {resetTreeNodes} from './groups-filter';
import {
  getGeoFenceFilterSelections,
  getItemAllowed,
  mapCrashToHVSEvent,
  updateUserPreference
} from './helpers';
import {setSideSheetStatus} from './nav';
import {
  APPLY_DEVICES_FILTER,
  applyAssetHistoryDateFilter,
  clearFilters,
  DATE_FILTER_REQUEST,
  initializeReports
} from './reports';
import {toggleUserPrompt} from './user-prompt';

export const NAME                                         = 'tracking';
export const INITIALIZE_TRACKING_ASSETS                   = 'INITIALIZE_TRACKING_ASSETS';
export const INITIALIZE_TRACKING                          = 'INITIALIZE_TRACKING';
export const SET_LARGE_FLEET_MODE                         = 'SET_LARGE_FLEET_MODE';
export const RESET_INITIALIZE_TRACKING                    = 'RESET_INITIALIZE_TRACKING';
export const RE_INITIALIZE_TRACKING                       = 'RE_INITIALIZE_TRACKING';
export const INITIALIZE_OPERATIONAL_LAYERS                = 'INITIALIZE_OPERATIONAL_LAYERS';
export const RESET_TRACKING                               = 'RESET_TRACKING';
export const MAP_INITIALIZED                              = 'MAP_INITIALIZED';
export const TRACKING_SEARCH_REQUEST                      = 'TRACKING_SEARCH_REQUEST';
export const TRACKING_SEARCH_RESPONSE                     = 'TRACKING_SEARCH_RESPONSE';
export const TRACKING_ASSETS_REQUEST                      = 'TRACKING_ASSETS_REQUEST';
export const TRACKING_ASSETS_RESPONSE                     = 'TRACKING_ASSETS_RESPONSE';
export const SET_SELECTED_TRACKING_SEARCH_RESULT          = 'SET_SELECTED_TRACKING_SEARCH_RESULT';
export const TRACKING_SEARCH_ERROR                        = 'TRACKING_SEARCH_ERROR';
export const SET_HOVERED_ASSET                            = 'SET_HOVERED_ASSET';
export const SET_SELECTED_ASSET                           = 'SET_SELECTED_ASSET';
export const SET_LAST_SELECTED_ASSET                      = 'SET_LAST_SELECTED_ASSET';
export const SET_PANEL_TOP_POSITION                       = 'SET_PANEL_TOP_POSITION';
export const SET_ASSET_DETAIL_VIEW                        = 'SET_ASSET_DETAIL_VIEW';
export const SET_TRACKING_BASE_MAP                        = 'SET_TRACKING_BASE_MAP';
export const SET_TRACKING_MAP_LAYERS                      = 'SET_TRACKING_MAP_LAYERS';
export const SET_TRACKING_MAP_POSITION                    = 'SET_TRACKING_MAP_POSITION';
export const SET_TRACKING_MAP_VIEW                        = 'SET_TRACKING_MAP_VIEW';
export const SET_TRACKING_MAP_ZOOM                        = 'SET_TRACKING_MAP_ZOOM';
export const SET_TRACKING_MAP_EXTENT                      = 'SET_TRACKING_MAP_EXTENT';
export const SET_INITIAL_TRACKING_MAP_EXTENT              = 'SET_INITIAL_TRACKING_MAP_EXTENT';
export const SET_TRAFFIC_LAYER_OPACITY                    = 'SET_TRAFFIC_LAYER_OPACITY';
export const SET_TRAFFIC_LAYER_SUBLAYERS                  = 'SET_TRAFFIC_LAYER_SUBLAYERS';
export const SET_WEATHER_LAYER_OPACITY                    = 'SET_WEATHER_LAYER_OPACITY';
export const SET_GEOFENCE_LAYER_OPACITY                   = 'SET_GEOFENCE_LAYER_OPACITY';
export const GET_ASSET_TOOLS_REQUEST                      = 'GET_ASSET_TOOLS_REQUEST';
export const GET_ASSET_TOOLS_RESPONSE                     = 'GET_ASSET_TOOLS_RESPONSE';
export const GET_ASSET_TOOLS_ERROR                        = 'GET_ASSET_TOOLS_ERROR';
export const FIND_NEARBY_REQUEST                          = 'FIND_NEARBY_REQUEST';
export const FIND_NEARBY_RESPONSE                         = 'FIND_NEARBY_RESPONSE';
export const FIND_NEARBY_ERROR                            = 'FIND_NEARBY_ERROR';
export const EXIT_FIND_NEARBY                             = 'EXIT_FIND_NEARBY';
export const SET_TOOL_PANEL_OPEN                          = 'SET_TOOL_PANEL_OPEN';
export const SET_ASSIGNED_ASSETS_PANEL_OPEN               = 'SET_ASSIGNED_ASSETS_PANEL_OPEN';
export const SET_TOOL_DETAIL_VIEW                         = 'SET_TOOL_DETAIL_VIEW';
export const SET_SELECTED_TOOL                            = 'SET_SELECTED_TOOL';
export const SET_DISCONNECTED_TOOL_TAG_REPORT             = 'SET_DISCONNECTED_TOOL_TAG_REPORT';
export const SET_DISCONNECTED_TOOL_ERROR                  = 'SET_DISCONNECTED_TOOL_ERROR';
export const TOGGLE_MAP_CONTROLS                          = 'TOGGLE_MAP_CONTROLS';
export const TOGGLE_ASSET_CLUSTERING                      = 'TOGGLE_ASSET_CLUSTERING';
export const TOGGLE_ASSET_LABELLING                       = 'TOGGLE_ASSET_LABELLING';
export const SET_ASSET_CONTENT                            = 'SET_ASSET_CONTENT';
export const SET_PROCESSED_FILTERS                        = 'SET_PROCESSED_FILTERS';
export const SET_FILTER_NAME                              = 'SET_FILTER_NAME';
export const SET_SELECTED_RECORDS                         = 'SET_SELECTED_RECORDS';
export const SET_TRACKING_FILTERS                         = 'SET_TRACKING_FILTERS';
export const SET_GROUPS_FILTER_REQUEST                    = 'SET_GROUPS_FILTER_REQUEST';
export const SET_GROUPS_FILTER_RESPONSE                   = 'SET_GROUPS_FILTER_RESPONSE';
export const SET_GROUPS_FILTER_ERROR                      = 'SET_GROUPS_FILTER_ERROR';
export const SET_ASSETS_FILTER_REQUEST                    = 'SET_ASSETS_FILTER_REQUEST';
export const SET_ASSETS_FILTER_RESPONSE                   = 'SET_ASSETS_FILTER_RESPONSE';
export const SET_ASSETS_FILTER_ERROR                      = 'SET_ASSETS_FILTER_ERROR';
export const SET_DEVICES_FILTER_REQUEST                   = 'SET_DEVICES_FILTER_REQUEST';
export const SET_DEVICES_FILTER_RESPONSE                  = 'SET_DEVICES_FILTER_RESPONSE';
export const SET_DEVICES_FILTER_ERROR                     = 'SET_DEVICES_FILTER_ERROR';
export const SET_ASSET_TYPE_FILTER_REQUEST                = 'SET_ASSET_TYPE_FILTER_REQUEST';
export const SET_ASSET_TYPE_FILTER_RESPONSE               = 'SET_ASSET_TYPE_FILTER_RESPONSE';
export const SET_ASSET_TYPE_FILTER_ERROR                  = 'SET_ASSET_TYPE_FILTER_ERROR';
export const SET_CATEGORY_FILTER_REQUEST                  = 'SET_CATEGORY_FILTER_REQUEST';
export const SET_CATEGORY_FILTER_RESPONSE                 = 'SET_CATEGORY_FILTER_RESPONSE';
export const SET_CATEGORY_FILTER_ERROR                    = 'SET_CATEGORY_FILTER_ERROR';
export const SET_GEOFENCE_FILTER_OPEN                     = 'SET_GEOFENCE_FILTER_OPEN';
export const SET_GEOFENCE_FILTER_REQUEST                  = 'SET_GEOFENCE_FILTER_REQUEST';
export const SET_GEOFENCE_FILTER_RESPONSE                 = 'SET_GEOFENCE_FILTER_RESPONSE';
export const SET_ASSET_STATUS_FILTER                      = 'SET_ASSET_STATUS_FILTER ';
export const SET_GEOFENCE_FILTER_ERROR                    = 'SET_GEOFENCE_FILTER_ERROR ';
export const SET_SAVE_FILTER_REQUEST                      = 'SET_SAVE_FILTER_REQUEST';
export const SET_SAVE_FILTER_RESPONSE                     = 'SET_SAVE_FILTER_RESPONSE';
export const SET_SAVE_FILTER_ERROR                        = 'SET_SAVE_FILTER_ERROR';
export const SET_ROUTE_FILTER_REQUEST                     = 'SET_ROUTE_FILTER_REQUEST';
export const SET_ROUTE_FILTER_RESPONSE                    = 'SET_ROUTE_FILTER_RESPONSE';
export const SET_ROUTE_FILTER_ERROR                       = 'SET_ROUTE_FILTER_ERROR';
export const SET_ASSET_STATUS_FILTER_REQUEST              = 'SET_ASSET_STATUS_FILTER_REQUEST';
export const SET_ASSET_STATUS_FILTER_RESPONSE             = 'SET_ASSET_STATUS_FILTER_RESPONSE';
export const SET_ASSET_STATUS_FILTER_ERROR                = 'SET_ASSET_STATUS_FILTER_ERROR';
export const SET_FIND_NEARBY_POPUP_STATE                  = 'SET_FIND_NEARBY_POPUP_STATE';
export const SET_FIND_NEARBY_TOGGLE                       = 'SET_FIND_NEARBY_TOGGLE';
export const SET_FIND_NEARBY_ASSET                        = 'SET_FIND_NEARBY_ASSET';
export const SET_FIND_NEARBY_GEOFENCE                     = 'SET_FIND_NEARBY_GEOFENCE';
export const SET_LOCATION_FIND_NEARBY                     = 'SET_LOCATION_FIND_NEARBY';
export const SET_GEOFENCE_PANEL_OPEN                      = 'SET_GEOFENCE_PANEL_OPEN';
export const SET_FIND_NEARBY_LABEL                        = 'SET_FIND_NEARBY_LABEL';
export const SET_FIND_BUTTON_STATE                        = 'SET_FIND_BUTTON_STATE';
export const SET_SEARCH_HISTORY                           = 'SET_SEARCH_HISTORY';
export const SAVE_GEOFENCE                                = 'SAVE_GEOFENCE';
export const SAVING_GEOFENCE                              = 'SAVING_GEOFENCE';
export const SAVE_GEOFENCE_SUCCESS                        = 'SAVE_GEOFENCE_SUCCESS';
export const SAVE_GEOFENCE_ERROR                          = 'SAVE_GEOFENCE_ERROR';
export const RESET_GEOFENCE_STATUS                        = 'RESET_GEOFENCE_STATUS';
export const MAP_COTROLS_BOTTOM_SHEET_OPEN                = 'MAP_COTROLS_BOTTOM_SHEET_OPEN';
export const GET_ASSET_DETAILS_REQUEST                    = 'GET_ASSET_DETAILS_REQUEST';
export const GET_ASSET_DETAILS_RESPONSE                   = 'GET_ASSET_DETAILS_RESPONSE';
export const GET_ASSET_DETAILS_ERROR                      = 'GET_ASSET_DETAILS_ERROR';
export const SET_ASSET_DETAILS_LOADING                    = 'SET_ASSET_DETAILS_LOADING';
export const GET_ASSOCIATED_ASSETS_REQUEST                = 'GET_ASSOCIATED_ASSETS_REQUEST';
export const GET_ASSOCIATED_ASSETS_RESPONSE               = 'GET_ASSOCIATED_ASSETS_RESPONSE';
export const GET_ASSOCIATED_ASSETS_ERROR                  = 'GET_ASSOCIATED_ASSETS_ERROR';
export const GET_GROUPS_FILTER_REQUEST                    = 'GET_GROUPS_FILTER_REQUEST';
export const GET_GROUPS_FILTER_RESPONSE                   = 'GET_GROUPS_FILTER_RESPONSE';
export const GET_GROUPS_FILTER_ERROR                      = 'GET_GROUPS_FILTER_ERROR';
export const GET_ASSET_TYPE_AND_CATEGORY_FILTER_REQUEST   = 'GET_ASSET_TYPE_AND_CATEGORY_FILTER_REQUEST';
export const GET_ASSET_TYPE_AND_CATEGORY_FILTER_RESPONSE  = 'GET_ASSET_TYPE_AND_CATEGORY_FILTER_RESPONSE';
export const GET_ASSET_TYPE_AND_CATEGORY_FILTER_ERROR     = 'GET_ASSET_TYPE_AND_CATEGORY_FILTER_ERROR';
export const GET_GEOFENCE_FILTER_REQUEST                  = 'GET_GEOFENCE_FILTER_REQUEST';
export const GET_GEOFENCE_FILTER_RESPONSE                 = 'GET_GEOFENCE_FILTER_RESPONSE';
export const GET_GEOFENCE_FILTER_ERROR                    = 'GET_GEOFENCE_FILTER_ERROR';
export const GET_DISCONNECTED_TOOL_REPORT                 = 'GET_DISCONNECTED_TOOL_REPORT';
export const GET_DISCONNECTED_TOOL_ERROR                  = 'GET_DISCONNECTED_TOOL_ERROR';
export const APPLY_GEOFENCE_TO_FILTER                     = 'APPLY_GEOFENCE_TO_FILTER';
export const SET_GROUP_STATUS                             = 'SET_GROUP_STATUS';
export const SET_STATUS_TO_RESUME_MAP                     = 'SET_STATUS_TO_RESUME_MAP';
export const SET_ALL_FILTERS_LOADING_STATE                = 'SET_ALL_FILTERS_LOADING_STATE';
export const SET_AUTO_REFRESH_STATE                       = 'SET_AUTO_REFRESH_STATE';
export const SET_ASSET_GRID_UPDATE                        = 'SET_ASSET_GRID_UPDATE';
export const SET_GEOFENCE_BORDER_ONLY                     = 'SET_GEOFENCE_BORDER_ONLY';
export const SET_GEOFENCE_LABEL_STATE                     = 'SET_GEOFENCE_LABEL_STATE';
export const SET_OPERATIONAL_LAYER_VISIBILITY             = 'SET_OPERATIONAL_LAYER_VISIBILITY';
export const SET_OPERATIONAL_LAYER_OPACITY                = 'SET_OPERATIONAL_LAYER_OPACITY';
export const SET_OPERATIONAL_SUBLAYER_VISIBILITY          = 'SET_OPERATIONAL_SUBLAYER_VISIBILITY';
export const SET_OPERATIONAL_LAYERS                       = 'SET_OPERATIONAL_LAYERS';
export const SET_IS_LAYER_LIST_UPDATE                     = 'SET_IS_LAYER_LIST_UPDATE';
export const SET_LARGE_FLEET_ASSETS_AGGREGATION_LEVELS    = 'SET_LARGE_FLEET_ASSETS_AGGREGATION_LEVELS';
export const SET_LARGE_FLEET_STATE                        = 'SET_LARGE_FLEET_STATE';
export const RESET_LARGE_FLEET_STATE                      = 'RESET_LARGE_FLEET_STATE';
export const REFRESH_LARGE_FLEET_STATE                    = 'REFRESH_LARGE_FLEET_STATE';
export const GET_LARGE_FLEET_REQUEST                      = 'GET_LARGE_FLEET_REQUEST';
export const GET_LARGE_FLEET_RESPONSE                     = 'GET_LARGE_FLEET_RESPONSE';
export const RESET_FETCH_LARGE_FLEET_ASSETS               = 'RESET_FETCH_LARGE_FLEET_ASSETS';
export const GET_LARGE_FLEET_ERROR                        = 'GET_LARGE_FLEET_ERROR';
export const TOGGLE_MOBILE_FILTERS                        = 'TOGGLE_MOBILE_FILTERS';
export const SET_ASSET_SELECTION_TYPE                     = 'SET_ASSET_SELECTION_TYPE';
// retrieve Large fleet Grid ...
export const GET_LARGE_FLEET_GRID_REQUEST                  = 'GET_LARGE_FLEET_GRID_REQUEST';
export const GET_LARGE_FLEET_GRID_RESPONSE                 = 'GET_LARGE_FLEET_GRID_RESPONSE';
export const GET_LARGE_FLEET_GRID_ERROR                    = 'GET_LARGE_FLEET_GRID_ERROR';
export const RESET_GRID_FORCE_UPDATE                       = 'RESET_GRID_FORCE_UPDATE';
// History
export const GET_ASSETS_HISTORY_REQUEST                    = 'GET_ASSETS_HISTORY_REQUEST';
export const GET_ASSETS_HISTORY_RESPONSE                   = 'GET_ASSETS_HISTORY_RESPONSE';
export const GET_ASSETS_HISTORY_ERROR                      = 'GET_ASSETS_HISTORY_ERROR';
export const SET_ASSETS_HISTORY_UPDATES_LIVE               = 'SET_ASSETS_HISTORY_UPDATES_LIVE';
export const SET_ASSETS_HISTORY_LIVE_INTERVAL              = 'SET_ASSETS_HISTORY_LIVE_INTERVAL';
export const SET_ASSETS_HISTORY_ERROR                      = 'SET_ASSETS_HISTORY_ERROR';
export const SET_ASSETS_HISTORY_REPORT_TYPE                = 'SET_ASSETS_HISTORY_REPORT_TYPE';
export const SET_ASSETS_HISTORY_INPUT_EVENTS               = 'SET_ASSETS_HISTORY_INPUT_EVENTS';
export const SHOW_ASSET_HISTORY_REFRESH_MESSAGE            = 'SHOW_ASSET_HISTORY_REFRESH_MESSAGE';
export const SET_HISTORY_REFRESH                           = 'SET_HISTORY_REFRESH';
export const RESET_ASSETS_HISTORY                          = 'RESET_ASSETS_HISTORY';
export const RESET_ASSETS_TRACKING                         = 'RESET_ASSETS_TRACKING';
// Asset Table View
export const SET_SIDESHEET_VIEW                            = 'SET_SIDESHEET_VIEW';
export const SET_MAP_ONLY_VIEW                             = 'SET_MAP_ONLY_VIEW';
export const SET_GRID_COLUMNS                              = 'SET_TRACKING_ASSETS_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                           = 'UPDATE_TRACKING_ASSETS_GRID_COLUMNS';
export const SET_GRID_FILTERED_ASSET_IDS                   = 'SET_GRID_FILTERED_ASSET_IDS';
export const SET_TRACKING_GRID_FILTER_MODEL                = 'SET_TRACKING_GRID_FILTER_MODEL';
export const SET_TRACKING_GRID_STATE                       = 'SET_TRACKING_GRID_STATE';
export const SET_LARGE_FLEET_GRID_COUNT                    = 'SET_LARGE_FLEET_GRID_COUNT';
export const SET_BOTTOM_GRID_HEIGHT                        = 'SET_BOTTOM_GRID_HEIGHT';
// Smarter Trailer
export const LOAD_SMARTER_TRAILER_REQUEST                  = 'LOAD_SMARTER_TRAILER_REQUEST';
export const LOAD_SMARTER_TRAILER_RESPONSE                 = 'LOAD_SMARTER_TRAILER_RESPONSE';
export const LOAD_SMARTER_TRAILER_ERROR                    = 'LOAD_SMARTER_TRAILER_ERROR';
export const UPDATE_SMARTER_TRAILER_REQUEST                = 'UPDATE_SMARTER_TRAILER_REQUEST';
export const UPDATE_SMARTER_TRAILER_RESPONSE               = 'UPDATE_SMARTER_TRAILER_RESPONSE';
export const UPDATE_SMARTER_TRAILER_ERROR                  = 'UPDATE_SMARTER_TRAILER_ERROR';
export const ADD_DEVICE_WITH_SMARTER_TRAILER               = 'ADD_DEVICE_WITH_SMARTER_TRAILER';
export const SMARTER_TRAILER_CHECKED                       = 'SMARTER_TRAILER_CHECKED';
export const SMARTER_TRAILER_SENSOR_NEEDS_UPDATE           = 'SMARTER_TRAILER_SENSOR_NEEDS_UPDATE';
// 24-hour Asset Detail History tab (includes grid)
export const INITIALIZE_ASSET_DETAIL_HISTORY               = 'INITIALIZE_ASSET_DETAIL_HISTORY';
export const SET_TRACKING_MAP_IS_ASSET_DETAIL_HISTORY      = 'SET_TRACKING_MAP_IS_ASSET_DETAIL_HISTORY';
export const LOAD_ASSET_DETAIL_HISTORY_ERROR               = 'LOAD_ASSET_DETAIL_HISTORY_ERROR';
export const LOAD_ASSET_DETAIL_HISTORY_REQUEST             = 'LOAD_ASSET_DETAIL_HISTORY_REQUEST';
export const LOAD_ASSET_DETAIL_HISTORY_RESPONSE            = 'LOAD_ASSET_DETAIL_HISTORY_RESPONSE';
export const LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_REQUEST  = 'LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_REQUEST';
export const LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_RESPONSE = 'LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_RESPONSE';
export const LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_ERROR    = 'LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_ERROR';
export const SET_ASSET_HISTORY_GRID_COLUMNS                = 'SET_ASSET_HISTORY_GRID_COLUMNS';
export const UPDATE_ASSET_HISTORY_GRID_COLUMNS             = 'UPDATE_ASSET_HISTORY_GRID_COLUMNS';
export const TOGGLE_ASSET_HISTORY_GRID_EXPANDED_GROUPS     = 'TOGGLE_ASSET_HISTORY_GRID_EXPANDED_GROUPS';
export const SET_HOVERED_ASSET_DETAIL_HISTORY_EVENT        = 'SET_HOVERED_ASSET_DETAIL_HISTORY_EVENT';
// Set tracking history reporting
export const SET_TRACKING_HISTORY_REPORT                   = 'SET_TRACKING_HISTORY_REPORT';
export const SET_HISTORY_SELECTED_ASSET                    = 'SET_HISTORY_SELECTED_ASSET';
export const SET_LOADER_INDICATOR_STATUS                   = 'SET_LOADER_INDICATOR_STATUS';
export const SET_ASSETS_COUNT                              = 'SET_ASSETS_COUNT';
// SC Devices
export const LOAD_SC_DEVICES_REQUEST                      = 'LOAD_SC_DEVICES_REQUEST';
export const LOAD_SC_DEVICES_RESPONSE                     = 'LOAD_SC_DEVICES_RESPONSE';
export const LOAD_SC_DEVICES_ERROR                        = 'LOAD_SC_DEVICES_ERROR';
export const LOAD_SC_DEVICES_UPDATES                      = 'LOAD_SC_DEVICES_UPDATES';
export const RESET_SC_DEVICES                             = 'RESET_SC_DEVICES';
export const SET_SC_DEVICES_OPACITY                       = 'SET_SC_DEVICES_OPACITY';
export const SET_DEVICE_LABEL_CONTENT                     = 'SET_DEVICE_LABEL_CONTENT';
export const TOGGLE_DEVICE_LABELLING                      = 'TOGGLE_DEVICE_LABELLING';
export const SET_EDITABLE_DEVICE                          = 'SET_EDITABLE_DEVICE';

export const SET_MAP_VIEWS                                = 'SET_MAP_VIEWS';

// Boundry filter controls
export const SET_BOUNDARY_FILTER                          = 'SET_BOUNDARY_FILTER';

export const SET_ROUTE_MATCHING_ENABLED                   = 'SET_ROUTE_MATCHING_ENABLED';
export const SET_ION_VISION_ENABLED                       = 'SET_ION_VISION_ENABLED';
export const SET_CRASH_BOXX_ENABLED                       = 'SET_CRASH_BOXX_ENABLED';

//Assets List Display panel data
export const SET_ASSETS_SORT_DESCENDING                   = 'SET_ASSETS_SORT_DESCENDING';
export const TRACKING_ASSETS_UPDATES                      = 'TRACKING_ASSETS_UPDATES';
export const APPLY_DATE                                   = 'APPLY_DATE';
export const SET_TRACKING_MODE                            = 'SET_TRACKING_MODE';
export const SET_SELECTED_FILTERS_ACTIVE                  = 'SET_SELECTED_FILTERS_ACTIVE';
export const CLEAR_SELECTED_FILTERS                       = 'CLEAR_SELECTED_FILTERS';
export const SET_TRACKING_ASSET_COUNT_LOAD                = 'SET_TRACKING_ASSET_COUNT_LOAD';
export const SET_TRACKING_ASSET_COUNT                     = 'SET_TRACKING_ASSET_COUNT';
export const SET_TRACKING_GRID_FILTERED_ASSETS            = 'SET_TRACKING_GRID_FILTERED_ASSETS';

// Tracking Map Extents
export const SET_TRACKING_MAPVIEW_INITIAL_EXTENT          = 'SET_TRACKING_MAPVIEW_INITIAL_EXTENT';
export const SET_TRACKING_MAPVIEW_EXTENT                  = 'SET_TRACKING_MAPVIEW_EXTENT';

// FM Tracking
export const SET_FM_ASSET_INITIALIZED                     = 'SET_FM_ASSET_INITIALIZED';
export const SET_GEOFENCE_LOADED                          = 'SET_GEOFENCE_LOADED';

export const SET_FLEET_UPDATE_FLAG                        = 'SET_FLEET_UPDATE_FLAG';
export const SET_FLEET_UPDATE_RESET_FLAG                  = 'SET_FLEET_UPDATE_RESET_FLAG';
export const SET_FLEET_UPDATE_FETCHING                    = 'SET_FLEET_UPDATE_FETCHING';

// Find Nearby V2
export const SET_FIND_NEARBY_PANEL_ACTIVE                 = 'SET_FIND_NEARBY_PANEL_ACTIVE';
export const SET_FIND_NEARBY_DISTANCE                     = 'SET_FIND_NEARBY_DISTANCE';
export const SET_FIND_NEARBY_FROM_ITEM                    = 'SET_FIND_NEARBY_FROM_ITEM';
export const SET_FIND_NEARBY_RESULTS                      = 'SET_FIND_NEARBY_RESULTS';
export const SET_FIND_NEARBY_ACTIVE_STATUS                = 'SET_FIND_NEARBY_ACTIVE_STATUS';
export const SET_FIND_NEARBY_REFRESH                      = 'SET_FIND_NEARBY_REFRESH';
export const SET_FIND_NEARBY_SELECTED_ASSET               = 'SET_FIND_NEARBY_SELECTED_ASSET';
export const SET_MAP_WIDGET_SELECTED                      = 'SET_MAP_WIDGET_SELECTED';

// Live History V2
export const RUN_LIVE_HISTORY                             = 'RUN_LIVE_HISTORY';
export const SET_HISTORY_RINGS                            = 'SET_HISTORY_RINGS';
export const SET_PERSIST_LIVE_HISTORY                     = 'SET_PERSIST_LIVE_HISTORY';
export const CANCEL_LIVE_HISTORY                          = 'CANCEL_LIVE_HISTORY';
export const CLEAR_HISTORY_SEARCH_ASSET                   = 'CLEAR_HISTORY_SEARCH_ASSET';
export const SET_LIVE_HISTORY_ERROR                       = 'SET_LIVE_HISTORY_ERROR';
export const SET_LIVE_HISTORY_REPORT_TYPE                 = 'SET_LIVE_HISTORY_REPORT_TYPE';
export const SET_LIVE_HISTORY_SEARCH_TIME                 = 'SET_LIVE_HISTORY_SEARCH_TIME';
export const SET_LIVE_HISTORY_INPUT_EVENTS                = 'SET_LIVE_HISTORY_INPUT_EVENTS';
export const SET_LIVE_HISTORY_INITIAL_ASSETS              = 'SET_LIVE_HISTORY_INITIAL_ASSETS';
export const SET_LIVE_HISTORY_SELECTED_ASSET              = 'SET_LIVE_HISTORY_SELECTED_ASSET';
export const SET_LIVE_HISTORY_ASSET_SEARCH_LIST           = 'SET_LIVE_HISTORY_ASSET_SEARCH_LIST';
export const SET_LIVE_HISTORY_ASSET_SEARCH_ACTIVE         = 'SET_LIVE_HISTORY_ASSET_SEARCH_ACTIVE';
export const CLEAR_LIVE_HISTORY_ASSET_SEARCH_LIST         = 'CLEAR_LIVE_HISTORY_ASSET_SEARCH_LIST';
export const SET_LIVE_HISTORY_ASSET_FETCHING              = 'SET_LIVE_HISTORY_ASSET_FETCHING';
export const SET_LIVE_HISTORY_ASSET_UI_FETCHING           = 'SET_LIVE_HISTORY_ASSET_UI_FETCHING';
export const SET_LIVE_HISTORY_IO_PROFILE_FETCHING         = 'SET_LIVE_HISTORY_IO_PROFILE_FETCHING';
export const SET_LIVE_HISTORY_HIDE_NON_SELECTED_ASSETS    = 'SET_LIVE_HISTORY_HIDE_NON_SELECTED_ASSETS';

export const SET_FLEET_LAYER_PERSISTENCE_ACTIVE           = 'SET_FLEET_LAYER_PERSISTENCE_ACTIVE';

// context menu
export const SET_CONTEXT_MENU_LOCATION                    = 'SET_CONTEXT_MENU_LOCATION';
export const SET_CONTEXT_MENU_VISIBILITY                  = 'SET_CONTEXT_MENU_VISIBILITY';
export const SET_BOUNDARY_FILTER_MODE                     = 'SET_BOUNDARY_FILTER_MODE';

export const SET_TRACKING_ASSET_SNAP_TO_ROAD              = 'SET_TRACKING_ASSET_SNAP_TO_ROAD';
export const SET_TRACKING_ASSET_UPDATES_PLAYBACK_STATE    = 'SET_TRACKING_ASSET_UPDATES_PLAYBACK_STATE';

export const SET_TIME_MODE                                = 'SET_TIME_MODE';
export const SET_TIMELINE_PRESET                          = 'SET_TIMELINE_PRESET';
export const SET_TIMELINE_DURATION                        = 'SET_TIMELINE_DURATION';
export const SET_HISTORY_PRESET                           = 'SET_HISTORY_PRESET';
export const SET_HISTORY_FROM                             = 'SET_HISTORY_FROM';
export const SET_HISTORY_TO                               = 'SET_HISTORY_TO';
export const SET_HISTORY_ACTIVE                           = 'SET_HISTORY_ACTIVE';
export const SET_TIME_MODE_PANEL_OPEN                     = 'SET_TIME_MODE_PANEL_OPEN';

export const setTimeMode = mode => ({
  type : SET_TIME_MODE, mode
});

export const setTimelinePreset = preset => ({
  type : SET_TIMELINE_PRESET, preset
});

export const setTimelineDuration = duration => ({
  type : SET_TIMELINE_DURATION, duration
});

export const setHistoryPreset = preset => ({
  type : SET_HISTORY_PRESET, preset
});

export const setHistoryFrom = from => ({
  type : SET_HISTORY_FROM, from
});

export const setHistoryTo = to => ({
  type : SET_HISTORY_TO, to
});

export const setHistoryActive = isActive => ({
  type : SET_HISTORY_ACTIVE, isActive
});

export const setTimeModePanelOpen = isOpen => ({
  type : SET_TIME_MODE_PANEL_OPEN, isOpen
});

export const setTrackingAssetUpdatesPlaybackState = state => ({
  type : SET_TRACKING_ASSET_UPDATES_PLAYBACK_STATE, state
});

export const setTrackingAssetSnapToRoad = isSnapToRoadEnabled => ({
  type : SET_TRACKING_ASSET_SNAP_TO_ROAD, isSnapToRoadEnabled
});

export const setContextMenuLocation = payload => {
  const {visibility, ...rest} = payload;
  return ({
    type : SET_CONTEXT_MENU_LOCATION, location : rest, visibility
  });
};

export const setContextMenuVisibility = visibility => ({
  type : SET_CONTEXT_MENU_VISIBILITY, visibility
});

export const setBoundaryFilterMode = mode => ({
  type : SET_BOUNDARY_FILTER_MODE, mode
});
// context menu end...

export const setFleetLayerPersistenceActive = status => ({
  type : SET_FLEET_LAYER_PERSISTENCE_ACTIVE, status
});

export const runLiveHistory = (rings = null) => ({
  type : RUN_LIVE_HISTORY, rings
});

export const setHistoryRings = (rings) => ({
  type : SET_HISTORY_RINGS, rings
});

export const setPersistLiveHistory = (status) => ({
  type : SET_PERSIST_LIVE_HISTORY, status
});

export const cancelLiveHistory = () => ({
  type : CANCEL_LIVE_HISTORY
});

export const clearHistorySearchAssets = () => ({
  type : CLEAR_HISTORY_SEARCH_ASSET
});

export const setLiveHistoryHideNonSelectedAssets = status => ({
  type : SET_LIVE_HISTORY_HIDE_NON_SELECTED_ASSETS, status
});

export const setLiveHistoryFetching = isFetching => ({
  type : SET_LIVE_HISTORY_ASSET_FETCHING, isFetching
});

export const setLiveHistoryUIFetching = isUIFetching => ({
  type : SET_LIVE_HISTORY_ASSET_UI_FETCHING, isUIFetching
});

export const setLiveHistoryIOProfileFetching = isIOProfileFetching => ({
  type : SET_LIVE_HISTORY_IO_PROFILE_FETCHING, isIOProfileFetching
});

export const setLiveHistoryInitialAssets = assets => ({
  type : SET_LIVE_HISTORY_INITIAL_ASSETS, assets
});

export const setLiveHistoryError = (showError: boolean, assetCount: number) => ({
  type : SET_LIVE_HISTORY_ERROR, showError : showError, assetCount : assetCount
});

export const clearLiveHistoryAssetSearchList = selectedIds => ({
  type : CLEAR_LIVE_HISTORY_ASSET_SEARCH_LIST, selectedIds
});

export const setLiveHistorySelectedAsset = selectedAsset => ({
  type : SET_LIVE_HISTORY_SELECTED_ASSET, selectedAsset
});

/**
 * Asynchronously processes a list of assets to populate each asset's `inputs` field
 * based on their associated IO profiles, and updates the live history asset list in the store.
 *
 * - Fetches detailed asset data for each item.
 * - Loads IO profiles if available and extracts input port names.
 * - Dispatches updates for each processed asset.
 * - Marks IO profile fetching as complete after processing all assets.
 */
export const setLiveHistoryAssetList = assets => {
  return async(dispatch, getState) => {
    for (let i = 0; i < assets.length; i++) {
      const originalAsset = assets[i];
      const asset = {
        ...originalAsset,
        inputs : []
      };
      const ctcAsset = await getAsset(asset?.id);
      const data = {
        accountId        : getAccountId(getState),
        pageId           : 1,
        pageSize         : 1,
        includeSubGroups : true
      };
      if (ctcAsset?.ioProfile) {
        data.filterOn = {id: entityId(ctcAsset?.ioProfile.href)};
        const {ioprofiles} = await loadIOProfiles(data);
        if (ioprofiles?.length) {
          ioprofiles[0]?.ports?.forEach(({name}) => {
            asset.inputs.push(name);
          });
        }
      }
      dispatch(setLiveHistoryAssetSearchList(asset));
    }
    dispatch(setLiveHistoryIOProfileFetching(false));
  };
};

export const setLiveHistoryAssetSearchList = (asset, isBulk = false) => ({
  type : SET_LIVE_HISTORY_ASSET_SEARCH_LIST, asset, isBulk
});

export const setLiveHistoryAssetSearchActive = active => ({
  type : SET_LIVE_HISTORY_ASSET_SEARCH_ACTIVE, active
});

export const setLiveHistoryReportType = reportType => ({
  type : SET_LIVE_HISTORY_REPORT_TYPE, reportType
});

export const setLiveHistorySearchTime = time => ({
  type : SET_LIVE_HISTORY_SEARCH_TIME, time
});

export const setLiveHistoryInputEvents = inputEvents => ({
  type : SET_LIVE_HISTORY_INPUT_EVENTS, inputEvents
});

export const setMapWidgetSelected = selected => ({
  type : SET_MAP_WIDGET_SELECTED, selected
});

export const setFindNearbyResults = results => ({
  type : SET_FIND_NEARBY_RESULTS, results
});

export const setFindNearbyActive = isActive => ({
  type : SET_FIND_NEARBY_ACTIVE_STATUS, isActive
});

export const setFindNearbyPanelActive = isActive => ({
  type : SET_FIND_NEARBY_PANEL_ACTIVE, isActive
});

export const setFindNearbyDistance = distance => ({
  type : SET_FIND_NEARBY_DISTANCE, distance
});

export const setFindNearbyFromItem = fromItem => ({
  type : SET_FIND_NEARBY_FROM_ITEM, fromItem
});

export const setFindNearbyRefresh = refresh => ({
  type : SET_FIND_NEARBY_REFRESH, refresh
});

export const setFindNearbySelectedAsset = selectedAsset => ({
  type : SET_FIND_NEARBY_SELECTED_ASSET, selectedAsset
});

export const setFMAssetInitialized = (isFMAssetInitialized: boolean) => ({
  type : SET_FM_ASSET_INITIALIZED, isFMAssetInitialized
});

export const setGeofenceLoaded = (isGeofenceLoaded: boolean) => ({
  type : SET_GEOFENCE_LOADED, isGeofenceLoaded
});

export const resetGeofenceStatus = (geofenceStatus) => ({
  type : RESET_GEOFENCE_STATUS, geofenceStatus
});
export const attributeMappings = {
  operatorNamesUI      : 'operatorNames',
  typeUI               : 'assetType',
  eventTimeUI          : 'eventTime',
  latestEventUI        : 'eventType',
  eventTypeUI          : 'eventType',
  odometerUI           : 'odometer',
  gpsOdometerUI        : 'gpsOdometer',
  vbOdometerUI         : 'vbOdometer',
  engineHourUI         : 'engineHour',
  engineHoursGPSUI     : 'engineHours',
  fuelTypeUI           : 'fuelType',
  idleHoursUI          : 'idleHours',
  engineLightUI        : 'vbusMilStatus',
  vbatIntUI            : 'adcDeviceBatteryVoltage',
  vbatExtUI            : 'adcExternalPowerVoltage',
  batteryUI            : 'evBatteryPackRemainingCharge',
  ignitionUI           : 'ignition',
  input1UI             : 'input1',
  input2UI             : 'input2',
  input3UI             : 'input3',
  input4UI             : 'input4',
  input5UI             : 'input5',
  input6UI             : 'input6',
  input7UI             : 'input7',
  gpsValidityUI        : 'gpsValidity',
  commStateAvailableUI : 'commStateAvailable',
  gpsSpeedUI           : 'gpsSpeed',
  vbSpeedUI            : 'vbSpeed',
  latitudeUI           : 'latitude',
  longitudeUI          : 'longitude',
  speedUI              : 'speed',
  addressUI            : 'street',
  altitudeUI           : 'altitude',
  routeNameUI          : 'routeName',
  assetNameUI          : 'assetName',
  deviceEsnUI          : 'deviceIdValue',
  location             : 'location',
  deviceNameUI         : 'deviceName',
  deviceType           : 'deviceType'
};

// invokes when the loading need to be show/hide in ResultModules...
export const setTrackingAssetCountLoad = (state) => ({
  type : SET_TRACKING_ASSET_COUNT_LOAD, state
});

// invokes to set the mode of Tracking pages...
export const setIsLargeFleetMode = (isHFM: boolean) => ({
  type : SET_LARGE_FLEET_MODE, isHFM
});

/**
 * Defines the limit results of the Tracking Search query
 */
const SEARCH_LIMIT_RESULTS = 100;

const DATETIME_PROPERTY = '_dbWriteTime';

interface MediaProps {
  isDesktop?: boolean;
  isPhone?: boolean;
};

interface CargoParamsProps {
  getState: any,
  selectedAssets: CVSAssetType[],
  selectedDevices: Device[],
  globalGroupHierarchy: any,
  geometry?: number[][]
};

interface SelectedAssetProps {
  asset: Devices,
  coordinates?: object,
  useCtc?: boolean,
  resetTool?: boolean
};

export interface LaunchSHRProps {
  selectedDevice: Device,
  redirectPage?: boolean;
  date?: string | Date;
};

interface UpdateSelectedAsset {
  selectedAsset: Devices,
  assets: CVSAssetType[],
  isMapSelection?: boolean,
  panMapOnly?: boolean,
  tracking2Page?: boolean
}

// local util to identify the currently page is tracking 2
const isTracking2 = (getState: Function): boolean => {
  const {
    nav: {
      currentPage
    }
  } = getState();
  return isCurrentPage(currentPage, Route.TRACKING2);
};

const getSortByAssetName = (order: boolean) => {
  return `assetName ${!order ? 'DESC' : 'ASC'}`;
};

export const resetInitializeTracking = () => ({
  type : RESET_INITIALIZE_TRACKING
});

export const reInitializeTracking = () => ({
  type : RE_INITIALIZE_TRACKING
});

export const resetTrackingAssetsState = () => {
  return async(dispatch) => {
    dispatch({type: RESET_ASSETS_TRACKING});
  };
};

/**
 * Action creators for 24-hour Asset Detail History tab (includes grid):
 *
 * - Helper to filter event history.
 */
const getFilteredEventHistory = (history: HVSRecords, selectedEvents: EventCode[]) => {
  let filteredHistory: HVSRecords;
  if (history?.length) {
    const historyCopy = cloneDeep(history);
    filteredHistory = historyCopy.reduce((acc, item) => {
      const matchedEvent = Boolean(selectedEvents.find(({eventCode}) => eventCode === item.eventCode));
      item.gridVisibility = matchedEvent;
      item.mapVisibility  = matchedEvent || isIgnitionOff(item);
      return [...acc, item];
    }, []);
  }
  return filteredHistory;
};

/**
 * - Load 24-hour asset history
 *
 * Retrieves the given asset's history records for the provided start and end dates.
 * If the start (from) and end (to) arguments are not provided, the persisted start
 * and end dates from the reports state will be used. If the list of events to filter
 * by argument is not provided, the persisted list of events to filter by from the
 * tracking assetDetailHistoryGrid state will be used. If history records are returned,
 * a subsequent call will be made to retrieve the iOn Vision Events for the given time
 * span, and the resulting events will be added to the history records iOnVisionEvent
 * property.
 */
export const loadAssetDetailHistory = (asset: CVSAssetType, date?: GenericObject, selectedEvents?: EventCode[]) => {
  return async(dispatch, getState) => {
    let history: HVSRecords;
    let events: HVSRecords;
    let filteredHistory: HVSRecords;
    const {
      login : {
        user
      },
      tracking : {
        assetDetailHistoryGrid : {
          history : {
            eventFilter,
            startDate,
            endDate
          }
        }
      }
    } = getState();
    const start = date?.from || startDate;
    const end = date?.to || endDate;
    const filterEvents = selectedEvents || eventFilter || getDefaultEventList();
    dispatch({type: LOAD_ASSET_DETAIL_HISTORY_REQUEST, asset});
    try {
      history = await getAssetHistory({assets: [asset], start, end});
      const {results = [], totalCount} = history.length && hasCrashboxx(user)
        ? await dispatch(getCrashBoxxData({
          assetId          : asset.assetId,
          page             : 1,
          pageSize         : history.length,
          startDate        : start,
          endDate          : end,
          includeSubGroups : true
        }))
        : [];
      const crashEvents = totalCount ? mapCrashToHVSEvent(results): [];
      let group      = 1;
      let identifier = null;

      // add crash events to the HVS history
      history.push(...crashEvents);
      // sort the events so the crash events get inserted at the right moment
      history.sort((a, b) => a?.eventTime - b?.eventTime);

      history?.forEach((item, index) => {
        item.gridVisibility = true;
        item.mapVisibility  = true;
        // if ignition is off set the identifier as the index so it can be picked up
        // on the next pass to start a new group
        if (item.eventCode === 3) {
          identifier = index;
        }
        if ((identifier && (identifier + 1 === index))) {
          group      += 1;
          identifier = null;
        }
        item.tripGroup = group;
      });
      if (history?.length) {
        filteredHistory = getFilteredEventHistory(history, filterEvents);
        filteredHistory?.length > 0 ? history = filteredHistory : null;
      }
      events = hasIOnVision(user) ? await getiOnVisionDetails({
        deviceEsn : asset.deviceEsn,
        records   : history,
        start,
        end
      }) : null;
      dispatch({type: LOAD_ASSET_DETAIL_HISTORY_RESPONSE, history: (events || history)});
    } catch (error) {
      if (history) {
        dispatch({type: LOAD_ASSET_DETAIL_HISTORY_RESPONSE, history});
      } else {
        dispatch({type: LOAD_ASSET_DETAIL_HISTORY_ERROR, error});
      }
    }
  };
};

/**
 * Action creator that makes a call to the Crashboxx API through the Connect module
 * to get crash data for the given date range
 */
export const getCrashBoxxData = ({
  assetId,
  page,
  pageSize,
  startDate,
  endDate,
  sort = '-crashDate',
  filter = {},
  includeSubGroups = false
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const {
      login : {
        user : {
          account = {}
        } = {}
      } = {}
    } = getState();
    dispatch({type: LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_REQUEST});
    const from = (startDate ? moment(startDate) : moment().subtract(30, 'days')).toISOString();
    const to   = moment(endDate).endOf('day').toISOString();
    const searchData: any = {
      search : {
        searchTerms : {
          ...{
            accountId     : entityId(account),
            crashDate     : `[${from} TO ${to}]`,
            severityCode  : Object.values(CrashboxxSeverityCode).join(','),
            accountIdType : 'AccountType'
          },
          ...filter
        }
      }
    };
    if (assetId) {
      searchData.search.searchTerms.assetId = assetId;
    }
    try {
      const response = await loadCrashboxxData({
        searchData,
        page,
        pageSize,
        sort,
        includeSubGroups
      });
      dispatch({type: LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_RESPONSE, data: response});
      return response;
    } catch (err) {
      dispatch({type: LOAD_ASSET_DETAIL_HISTORY_CRASH_DATA_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * - Set grid column information.
 */
export const dispatchAssetHistorySetGridColumns = (columnState: ColumnState[]) => {
  return async(dispatch) => {
    dispatch(dispatchGridColumnsChange(columnState, NAME, SET_ASSET_HISTORY_GRID_COLUMNS));
  };
};

/**
 * Handle setting the loading status
 */
export const setTrackingLoadingStatus = (value) => {
  return {type: SET_LOADER_INDICATOR_STATUS, value};
};

/**
 * Handle setting the assets count
 */
export const setAssetsCount = (isLargeFleetMode, count) => {
  return {type: SET_ASSETS_COUNT, isLargeFleetMode, count};
};

/**
 * - Handle expanding and collapsing trip group rows in the grid.
 */
export const toggleAssetHistoryExpandedGroups = (id: string, isExpanded: boolean = false) => {
  return {type: TOGGLE_ASSET_HISTORY_GRID_EXPANDED_GROUPS, id, isExpanded};
};

/**
 * - Handle when an asset within the grid or map is hovered.
 */
export const setHoveredAssetDetailHistoryEvent = (event: HVSRecord | null) => ({
  type : SET_HOVERED_ASSET_DETAIL_HISTORY_EVENT, event
});

/**
 * - Set whether the tracking map is an asset detail history map.
 */
export const setTrackingMapIsAssetDetailHistory = (flag: boolean) => ({
  type : SET_TRACKING_MAP_IS_ASSET_DETAIL_HISTORY, flag
});

/**
 * setTrackingHistoryReport
 */
const setTrackingHistoryReport = routeHistory => ({
  type : SET_TRACKING_HISTORY_REPORT,
  routeHistory
});

/**
 * updateAssetTrackingHistory method checkes cached response for
 * given requestBody and make a request to HERE api if not stored.
 * It will update the redux store to cache the response from HERE api
 * for later usage.
 *
 * ```typescript
 * import {updateAssetTrackingHistory} from '@/store/actions/tracking';
 *
 * const response = await dispatch(updateAssetTrackingHistory(assets, requestBody));
 * ...
 *
 * ```
 *
 */
export const updateAssetTrackingHistory = (assetsMap: AssetsMap) => {
  return async(dispatch, getState) => {
    const groupIds = getCheckedAccountIds(getState);
    const {
      tracking : {
        trackingHistory : {
          routeHistory
        },
        history : {
          routeMatchingEnabled
        },
        filters: {
          assetTypes,
          assetCategories,
          geoFences,
          routes,
          assetStatus,
          assets: {
            selected: assetsSelected
          }
        }
      }
    } = getState();
    const {
      ALIGNED_ROUTES_CSV_HEADERS,
      ALIGNED_ROUTES_MAX_DISTANCE
    } = ALIGNED_ROUTES_CONFIG;
    const requestMap = {};
    const state      = cloneDeep(routeHistory);

    const assetTypesSelected      = [...getFilterRequestParams(assetTypes.selected)];
    const assetCategoriesSelected = [...getFilterRequestParams(assetCategories.selected)];
    const geoFencesSelected       = [...getFilterRequestParams(geoFences.selected)];
    const routesSelected          = [...getFilterRequestParams(routes.selected)];
    const assetStatusSelected     = [...getFilterRequestParams(assetStatus.selected)];

    const reponse = await getAssetCount({
      groups          : groupIds,
      assets          : getFilteredAssets(assetsSelected, groupIds),
      assetTypes      : assetTypesSelected,
      assetCategories : assetCategoriesSelected,
      geoFences       : geoFencesSelected,
      routes          : routesSelected,
      assetStatus     : assetStatusSelected
    });

    // loop through the assetsMap and check if request with same coordinates is
    // cached in the redux store, if not, assign request to the requestMap
    if (routeMatchingEnabled && isRouteMatchingEnabled(reponse.count)) {
      for (const key in assetsMap) {
        const requestBody = createCSV(assetsMap[key], ALIGNED_ROUTES_CSV_HEADERS);
        const result      = requestBody? routeHistory[requestBody] : null;
        if (!result) {
          requestMap[key] = assetsMap[key];
        }
      }
      try {
        if (!isEmpty(requestMap)) {
          const responses = (await massRouteMatching(requestMap) as any).map(({value}) => value);
          if (responses) {
            for (const asset of responses) {
              const {response, assetId} = asset;
              const nodes  = await createAlignedNodes(response, assetsMap[assetId], ALIGNED_ROUTES_MAX_DISTANCE);
              const routes       = createPathArray(response, nodes);
              const requestBody  = createCSV(assetsMap[assetId], ALIGNED_ROUTES_CSV_HEADERS);
              state[requestBody] = {nodes, routes};
            }
            dispatch(setTrackingHistoryReport(state));
          }
        }
      } catch (err) {
        error(err);
      }
    }
  };
};

/**
 * The getLargeFleetAssets method returns the asset information
 * from the Cluster end-point based on given parameters
 */
export const getLargeFleetAssets = ({
  groups,
  geometry = null,
  clusterDistanceInPixels,
  mapUnitsPerPixel
}) => {
  return async(dispatch, getState) => {
    const {
      tracking :
      {
        assets: {
          selectedAsset
        },
        fleetMode : {
          assetsAggregation: currentAssetsAggregation,
          isLargeFleetMode
        },
        filters : {
          assetTypes,
          assetCategories,
          geoFences,
          routes,
          assetStatus,
          assets : {
            selected : assetsSelected
          },
          boundary
        },
        maps : {
          layers
        },
        grid : {
          filterModel
        }
      }
    } = getState();

    if (isLargeFleetMode && layers?.includes(CURRENT_LAYER)) {
      const filter                           = filterModelConverterV2(filterModel, attributeMappings);
      const {statusFilter, addressFilterObj} = getAdditionalFilterParams(filter);
      const assetStatusParams                = [...getFilterRequestParams(assetStatus.selected)];
      statusFilter && assetStatusParams.push(statusFilter);
      const groupIds = getCheckedAccountIds(getState);
      const params = {
        groups : groups || getCheckedAccountIds(getState),
        assets : getFilteredAssets(assetsSelected, groupIds),
        ...(boundary?.length ? {
          geometry       : {rings: boundary, spatialReference: {wkid: 102100}},
          geometryType   : 'esriGeometryPolygon',
          returnGeometry : true,
          outSR          : 102100,
          spatialRel     : 'esriSpatialRelIntersects'
        }
          : {geometry}),
        assetCategories : [...getFilterRequestParams(assetCategories.selected)],
        assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
        geoFences       : [...getFilterRequestParams(geoFences.selected)],
        routes          : [...getFilterRequestParams(routes.selected)],
        assetStatus     : assetStatusParams,
        filter,
        ...addressFilterObj,
        clusterDistanceInPixels,
        mapUnitsPerPixel
      };
      dispatch({type: TRACKING_ASSETS_REQUEST});
      const response          = await getCVSClusters(params as CVSQueryArgs);
      const assetsAggregation = getAssetsAggregationsData(currentAssetsAggregation, response);
      if (selectedAsset) {
        const features = parseCollection(response, ['attributes']);
        dispatch(updateSelectedAsset({selectedAsset, assets: features, isMapSelection: true}));
      }
      dispatch({type: TRACKING_ASSETS_RESPONSE});
      dispatch({type: SET_LARGE_FLEET_ASSETS_AGGREGATION_LEVELS, assetsAggregation});
    }
  };
};

/**
 *
 * @function setGeofenceBorderOnly
 */
export const setGeofenceBorderOnly = (borderOnly: boolean) => {
  return {type: SET_GEOFENCE_BORDER_ONLY, borderOnly};
};

/**
 *
 * @function setGeofenceLabelState
 */
export const setGeofenceLabelState = (labelState: boolean) => {
  return {type: SET_GEOFENCE_LABEL_STATE, labelState};
};

export const setSelectedHistoryAsset = selectedAsset => ({
  type : SET_HISTORY_SELECTED_ASSET,
  selectedAsset
});

/**
 * @function setIsAutoRefresh
 */
export const setIsAutoRefresh = (isAutoRefresh: boolean) => {
  return async(dispatch) => {
    dispatch({type: SET_AUTO_REFRESH_STATE, isAutoRefresh});
  };
};

/**
 * @function setIsAssetGridUpdate
 */
export const setIsAssetGridUpdate = (isGridUpdate: boolean) => {
  return dispatch => {
    dispatch({type: SET_ASSET_GRID_UPDATE, hasUpdate: isGridUpdate});
  };
};

/**
 * @private
 */
export const resetLargeFleetAssets = () => {
  return {type: RESET_LARGE_FLEET_STATE};
};

/**
 * @private
 */
export const refreshLargeFleetAssets = (refresh) => {
  return async(dispatch, getState) => {
    const callsPage = [];
    dispatch({type: REFRESH_LARGE_FLEET_STATE, refresh});
    if (refresh) {
      const {
        tracking :
        {
          fleetMode : {
            largeFleetAssetList : {
              assets
            }
          }
        }
      } = getState();

      const assetLength = assets.length;
      const pages       = assetLength !== 0 ? assetLength / 100 : 1;
      const callsCount  = pages % 1 === 0 ? pages - 1 : Math.floor(pages);
      for (let i = 0; i <= callsCount; i++) {
        callsPage.push(i * 100);
      }
      await Promise.all(callsPage.map((page) => {
        return dispatch(getCVSPaginatedAssets({startRow: page, forceUpdate: refresh}));
      })).then(() => {
        dispatch({type: REFRESH_LARGE_FLEET_STATE, refresh: false});
      });
    }
  };
};

/**
 * @getGridCVSPaginatedAssets
 *
 * fetches assets for this account to be displayed in the
 * tracking Grid view
 */
export const getGridCVSPaginatedAssets = ({
  pageId = 1,
  pageSize = 8,
  sort,
  filter,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_LARGE_FLEET_GRID_REQUEST});
    try {
      const groupIds = getCheckedAccountIds(getState);
      const {
        tracking:
        {
          assets: {
            isAssetsSortDescending
          },
          filters: {
            assetTypes,
            assetCategories,
            geoFences,
            routes,
            assetStatus,
            assets: {
              selected: assetsSelected
            }
          }
        }
      } = getState();
      const params: CVSQueryArgs = {
        groups          : groupIds,
        assets          : getFilteredAssets(assetsSelected, groupIds),
        assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
        assetCategories : [...getFilterRequestParams(assetCategories.selected)],
        geoFences       : [...getFilterRequestParams(geoFences.selected)],
        routes          : [...getFilterRequestParams(routes.selected)],
        assetStatus     : [...getFilterRequestParams(assetStatus.selected)],
        startRow        : pageId > 0 ? ((pageId - 1) * pageSize) : pageId,
        pageSize
      };

      if (sort) {
        params.sort = sort || getSortByAssetName(isAssetsSortDescending);
      }

      if (filter) {
        params.filter = filter;
      }
      const {count} = await getAssetCount(params);
      const assets  = await loadCVSAssets(params);
      dispatch({type: GET_LARGE_FLEET_GRID_RESPONSE});
      callbackFn?.({items: assets, totalCount: count});

      return {items: assets, totalCount: count};
    } catch (err) {
      dispatch({type: GET_LARGE_FLEET_GRID_ERROR, err});
    }
  };
};

interface AppliedParams {
  groups?: number[];
  assets?: any;
  assetTypes?: string[];
  assetCategories?: string[];
  geoFences?: string[];
  routes?: string[];
  assetStatus?: string[];
}

interface PaginatedAssetsType {
  startRow?: number;
  pageSize?: number;
  forceUpdate?: boolean;
  sort?: string;
  filter?: string;
  callback?: Function;
  includeHFMCount?: boolean;
  params?: AppliedParams;
}

/**
 * getCVSPaginatedAssets
 *
 * @param startRow is the row number [1, 10, etc]
 * @param pageSize is the number of items per page
 */
export const getCVSPaginatedAssets = ({
  startRow = 0,
  pageSize = 100,
  forceUpdate = false,
  sort,
  filter,
  callback,
  includeHFMCount = true,
  params: appliedParameters = null
}: PaginatedAssetsType) => {
  return async(dispatch, getState) => {
    dispatch({type: GET_LARGE_FLEET_REQUEST});
    try {
      const groupIds = getCheckedAccountIds(getState);
      const {
        tracking:
        {
          sideSheetView,
          assets: {
            selectedAsset,
            isAssetsSortDescending
          },
          fleetMode : {
            isLargeFleetMode,
            isLargeFleetAssetsFetched,
            largeFleetAssetList : {
              refresh
            }
          },
          filters: {
            assetTypes,
            assetCategories,
            geoFences,
            routes,
            assetStatus,
            assets: {
              selected: assetsSelected
            } = {}, // Define assets object with a default value
            devices: {
              selected: selectedDevices
            } = {}, // Define devices object with a default value
            boundary
          },
          grid : {
            filterModel
          }
        }
      } = getState();
      if (!isLargeFleetAssetsFetched || startRow || forceUpdate) {
        const stringifiedFilterModel           = filterModelConverterV2(filterModel, attributeMappings);
        const filterToApply                    = filter?.length ? filter : stringifiedFilterModel;
        const {statusFilter, addressFilterObj} = getAdditionalFilterParams(filterToApply as any);
        if (isLargeFleetMode) {
          const {
            groups: appliedGroups,
            assets: appliedAssets,
            assetTypes: appliedAssetTypes,
            assetCategories: appliedCategories,
            geoFences: appliedGeoFences,
            routes: appliedRoutes,
            assetStatus: appliedStatus
          } = appliedParameters || {};
          const appliedAssetStatus = appliedStatus ?? [...getFilterRequestParams(assetStatus.selected)];
          statusFilter && appliedAssetStatus.push(statusFilter);
          const params = {
            groups          : appliedGroups ?? groupIds,
            assets          : appliedAssets ?? getFilteredAssets(assetsSelected, groupIds),
            assetTypes      : appliedAssetTypes ?? [...getFilterRequestParams(assetTypes.selected)],
            assetCategories : appliedCategories ?? [...getFilterRequestParams(assetCategories.selected)],
            geoFences       : appliedGeoFences ?? [...getFilterRequestParams(geoFences.selected)],
            routes          : appliedRoutes ?? [...getFilterRequestParams(routes.selected)],
            assetStatus     : Array.from(new Set(appliedAssetStatus)),
            filter          : filterToApply,
            geometry        : boundary,
            sort            : sort || getSortByAssetName(isAssetsSortDescending),
            startRow,
            pageSize,
            ...addressFilterObj
          };
          const response = await cacheOnFetch({
            queryKey : createKey(QueryKey.GET_CVS_ASSETS_COUNT, {
              groups          : params.groups,
              assets          : params.assets,
              assetTypes      : params.assetTypes,
              assetCategories : params.assetCategories,
              geoFences       : params.geoFences,
              routes          : params.routes,
              assetStatus     : params.assetStatus,
              filter          : params.filter,
              geometry        : params.geometry,
              ...addressFilterObj
            }, includeHFMCount),
            queryFn : async() => await getAssetCount(params)
          }) as AssetAndCountType;
          if (params.filter) {
            dispatch({type: SET_TRACKING_ASSET_COUNT, largeFleetMode: isLargeFleetMode, response});
          }
          const {
            count: totalCount,
            assets
          } = await getCVSAssetsAndCount({...params, count: response?.count, includeHFMCount});
          const assetsAsDevices = Devices.getAssetsFromCollection(assets);
          if (!startRow) {
            dispatch(resetLargeFleetAssets());
          }
          batch(() => {
            dispatch({
              type       : GET_LARGE_FLEET_RESPONSE,
              assets     : assetsAsDevices,
              updateGrid : (
                isSideSheetTableView(sideSheetView) ||
                isBottomSheetTableView(sideSheetView)
              ) && isNil(callback)
            });
            callback?.({items: assetsAsDevices, totalCount});
          });
          if (selectedAsset) {
            const {dbWriteTime, assetId} = getItemValue(selectedAsset);
            const updated = await getCVSAssets({
              filter : getDbWriteTimeFilter(dbWriteTime),
              assetId
            });
            dispatch(updateSelectedAsset({selectedAsset, assets: updated}));
          }

          dispatch(verifySelectedAsset(selectedAsset, true));
          refresh && dispatch({type: REFRESH_LARGE_FLEET_STATE, refresh: false});
          return {items: assets, totalCount};
        }
      }
    } catch (err) {
      dispatch({type: GET_LARGE_FLEET_ERROR, err});
    }
  };
};

/**
 * @function setGridChange
 */
export const setGridChange = () => {
  return async(dispatch, getState) => {
    try {
      const groupIds = getCheckedAccountIds(getState);
      const {
        tracking:
        {
          fleetMode: {
            isLargeFleetMode
          },
          filters: {
            assetCategories,
            assetTypes,
            geoFences,
            routes,
            assetStatus,
            assets: {
              selected: assetsSelected
            },
            devices
          },
          grid : {
            filterModel
          }
        }
      }          = getState();
      const filter                           = filterModelConverterV2(filterModel, attributeMappings);
      const {statusFilter, addressFilterObj} = getAdditionalFilterParams(filter);
      const assetStatusParams                = [...getFilterRequestParams(assetStatus.selected)];
      statusFilter && assetStatusParams.push(statusFilter);
      const {
        selected: selectedDevices
      } = devices || {};
      const params = {
        groups          : groupIds,
        assets          : getFilteredAssets(assetsSelected, groupIds),
        assetCategories : [...getFilterRequestParams(assetCategories.selected)],
        assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
        geoFences       : [...getFilterRequestParams(geoFences.selected)],
        routes          : [...getFilterRequestParams(routes.selected)],
        assetStatus     : assetStatusParams,
        filter,
        ...addressFilterObj
      };

      const response       = await getAssetCount(params);
      const totalCount = (response?.count ?? 0) + selectedDevices.length;
      const largeFleetMode = getIsLargeFleetMode(totalCount);
      batch(async() => {
        dispatch(setAssetsCount(largeFleetMode, totalCount));
        dispatch({type: SET_LARGE_FLEET_STATE, largeFleetMode: isLargeFleetMode, response, refreshMap: true});
        dispatch({type: TRACKING_ASSETS_RESPONSE});
      });
    } catch (error) {
      dispatch({type: GET_LARGE_FLEET_GRID_ERROR, error});
    }
  };
};
/**
 * updateLargeFleetAssetList
 *
 * @param currentMode is the current isLargeFleetMode state
 * @param nextMode is next isLargeFleetMode state
 */
const updateLargeFleetAssetList = (currentMode, nextMode, params = {}) => {
  return async(dispatch) => {
    dispatch({type: RESET_FETCH_LARGE_FLEET_ASSETS});
    if (currentMode && currentMode === nextMode) {
      dispatch(getCVSPaginatedAssets({params}));
    }
  };
};

/**
 * Defines a utiltiy to return array of extents
 */
const getextents = (response: any[]) => {
  const extents = [];
  if (response?.length) {
    response.forEach(res => {
      if (res?.count && res?.extent) {
        const {type, extent} = res || {};
        extents.push({type, extent});
      }
    });
  }
  return extents;
};

// Defines a utility to save the initial extent.
const setMapInitialExtent = (response: any[]) => {
  return async(dispatch) => {
    const extents = getextents(response);
    dispatch(setMapviewInitialExtent(extents));
  };
};

// Defines a utility to save the extent
const setMapExtent = (response: any[]) => {
  return async(dispatch) => {
    const extents = getextents(response);
    dispatch(setMapviewExtent(extents));
  };
};

/**
 * Defines a utility to clear all selections.
 */
export const clearAllSelections = (shouldDispatchSaveFilterApply = true) => {
  return async(dispatch, getState) => {
    const {
      tracking: {
        filters: filterState
      }
    } = getState();
    let newState = {};
    Object.keys(filterState).forEach(key => {
      newState = {
        ...newState,
        [key] : key === 'boundary' ? [] :{
          ...filterState[key],
          selected : []
        }
      };
    });
    if (shouldDispatchSaveFilterApply) {
      dispatch(applySaveFilter(null, newState, true));
    } else {
      dispatch({type: SET_SAVE_FILTER_REQUEST, value: {filterName: null, filterState: newState}});
    }
    dispatch(setSelectedFiltersActive(false));
    dispatch(clearQueryParams());
    dispatch(resetTreeNodes({mode: null}));
  };
};

/**
 * @function initializeTracking
 */
export const initializeTracking = (refreshAssetList: boolean = false) => {
  return async(dispatch, getState) => {
    const assetExtent = [];
    const {
      nav: {
        currentPage
      },
      tracking: {
        sideSheetView,
        isSelectedFiltersActive,
        appliedSaveFilter,
        maps : {
          extents : {
            initialExtent: mapviewInitialExtent
          }
        },
        filters: {
          assetTypes,
          assetCategories,
          geoFences,
          routes,
          assetStatus,
          devices: {
            selected: selectedDevices
          },
          assets: {
            selected: selectedAssets
          },
          boundary
        },
        grid: {
          filterModel
        }
      },
      globalGroup: {
        globalGroupHierarchy
      }
    } = getState();
    dispatch({type: TRACKING_ASSETS_REQUEST});
    dispatch(setTrackingAssetCountLoad(true));
    const groupIds = getCheckedAccountIds(getState);
    const params = {
      groups          : groupIds,
      assets          : getFilteredAssets(selectedAssets, groupIds),
      assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
      assetCategories : [...getFilterRequestParams(assetCategories.selected)],
      geoFences       : [...getFilterRequestParams(geoFences.selected)],
      routes          : [...getFilterRequestParams(routes.selected)],
      assetStatus     : [...getFilterRequestParams(assetStatus.selected)],
      geometry        : boundary
    };
    dispatch(clearSelectedTrackingSearchResult());
    const fleetResponse  = await getAssetCount(params);
    const totalCount     = (fleetResponse?.count ?? 0) + (selectedDevices?.length ?? 0);
    const largeFleetMode = getIsLargeFleetMode(totalCount);
    assetExtent.push(fleetResponse);
    if (!largeFleetMode && Features.hasSCIAccess()) {
      const cargoParams = getCargoParams({
        getState,
        selectedAssets,
        selectedDevices,
        globalGroupHierarchy
      });
      const cargoReponse = await getCargoCount(cargoParams);
      assetExtent.push(cargoReponse);
    }
    if (!mapviewInitialExtent?.length) {
      dispatch(setMapInitialExtent(assetExtent));
    } else if (isTrackingFilterActive(isSelectedFiltersActive, appliedSaveFilter)) {
      dispatch(setMapExtent(assetExtent));
    }
    let initialAssets = [];
    dispatch({type: TRACKING_ASSETS_UPDATES, updates: [], isAutoRefresh: false});
    if (!isCurrentPage(currentPage, Route.TRACKING2)) {
      dispatch(setAssetsCount(largeFleetMode, totalCount));
      dispatch({type: SET_LARGE_FLEET_STATE, largeFleetMode, response: fleetResponse});
      if (!largeFleetMode) {
        const assets = await getCVSAssets(params);
        initialAssets = Devices.getAssetsFromCollection(assets);
      } else {
        refreshAssetList && dispatch(getCVSPaginatedAssets({startRow: 0}));
      }
      if (largeFleetMode && !isSheetTableView(sideSheetView) && !isEmpty(filterModel)) {
        dispatch(setGridChange());
      }
    }
    dispatch({type: INITIALIZE_TRACKING, assets: initialAssets});
    dispatch({type: TRACKING_ASSETS_RESPONSE});
    dispatch({type: INITIALIZE_ASSET_DETAIL_HISTORY});
    dispatch({type: INITIALIZE_TRACKING_ASSETS});
  };
};

export const refreshListAssets = (pageSize: number = 100) => {
  return async(dispatch, getState) => {
    const {
      tracking:
      {
        assets: {
          isAssetsSortDescending
        },
        sideSheetView,
        filters: {
          assetTypes,
          assetCategories,
          geoFences,
          routes,
          assetStatus,
          assets: {
            selected: assetsSelected
          }
        },
        grid: {
          filterModel
        },
        fleetMode : {
          isLargeFleetMode,
          largeFleetAssetList : {
            assets: HFMAssets
          }
        }
      }
    } = getState();
    if (sideSheetView !== SideSheetView.bottomTable &&
    sideSheetView !== SideSheetView.table) {
      const groupIds = getCheckedAccountIds(getState);
      const filter   = filterModelConverterV2(filterModel, attributeMappings);
      const params = {
        groups          : groupIds,
        assets          : getFilteredAssets(assetsSelected, groupIds),
        assetTypes      : getFilterRequestParams(assetTypes.selected),
        assetCategories : getFilterRequestParams(assetCategories.selected),
        geoFences       : getFilterRequestParams(geoFences.selected),
        routes          : getFilterRequestParams(routes.selected),
        assetStatus     : getFilterRequestParams(assetStatus.selected),
        sort            : getSortByAssetName(isAssetsSortDescending),
        filter
      };
      const pages = HFMAssets?.length !== 0
        ? Math.ceil(HFMAssets?.length / pageSize)
        : 1;
      const pagePromises = Array.from(
        {length: pages}, (_, i) => {
          return new Promise((resolve, reject) => {
            loadCVSAssets({
              ...params,
              pageSize,
              startRow : i * pageSize
            } as any).then((data) => {
              resolve(data);
            });
          });
        });

      const assetsSets  = await Promise.all(pagePromises);
      const flatAssets = assetsSets.flat();
      const assetsAsDevices = Devices.getAssetsFromCollection(flatAssets);
      dispatch({type: GET_LARGE_FLEET_RESPONSE, assets: assetsAsDevices, isOverride: true});
    } else {
      dispatch({type: RESET_GRID_FORCE_UPDATE, update: true});
    }
  };
};

/**
 * showHistoryError
 *
 * @param showError is a boolean to display the dialog error
 * @param assetCount is the asset count from CVS Service
 *
 * setAssetHistoryError method helps to set the dialog error
 * visibility
 */
export const setAssetHistoryError = (showError: boolean, assetCount: number) => {
  return {type: SET_ASSETS_HISTORY_ERROR, showError: showError, assetCount: assetCount};
};

/**
 * setAssetHistoryReportType
 *
 * Defines a utility to set History Report Type
 */
export const setAssetHistoryReportType = (reportType: HistoryReportTypeEnum) => {
  return {type: SET_ASSETS_HISTORY_REPORT_TYPE, reportType};
};

/**
 * setAssetHistoryInputEvents
 *
 * Defines a utility to set History Inputs
 */
export const setAssetHistoryInputEvents = (inputEvents: HistoryInputEvents[]) => {
  return {type: SET_ASSETS_HISTORY_INPUT_EVENTS, inputEvents};
};

/**
 * resetAssetHistory
 *
 * it allows to reset Asset History state
 */
export const resetAssetHistory = () => {
  return {type: RESET_ASSETS_HISTORY};
};

/*
* Define a utilty to handle the visibility of the History Refresh Message
*/
export const showHistoryRefreshMessage = showMessage => ({
  type : SHOW_ASSET_HISTORY_REFRESH_MESSAGE,
  showMessage
});

/**
 * Defines a utility to return if the record is selected
 */
const isInputSelected = (inputs, item) => {
  if (inputs?.length) {
    const inputIndex = inputs?.findIndex(({label, isSelected}) =>
      label === item?.label && isSelected);
    return inputIndex >= 0;
  }
  return false;
};

/**
 * Defines a utility to get and store History Input Events
 */
export const setHistoryInputEvents = (assets: HVSRecords, isTracking2: boolean = false) => {
  return (dispatch, getState) => {
    const storeInputEvents = !isTracking2 ? setAssetHistoryInputEvents : setLiveHistoryInputEvents;
    if (assets?.length) {
      const {
        tracking: {
          historyV2: {
            inputEvents : inputsV2
          },
          history: {
            inputEvents: inputs
          }
        }
      } = getState();

      const inputsEvents = !isTracking2 ? inputs : inputsV2;
      const {None} = HistoryInputEventsEnum;
      const inputEvents   = [];
      const defaultInputs = Object.values(HistoryDefaultInputsEnum);
      const color         = Object.values(getInputEventColors());
      assets?.forEach(asset => {
        defaultInputs?.forEach(prop => {
          try {
            const input    = safeJSONParse(asset[prop]);
            const hasEvent = inputEvents.findIndex(({label}) => label === input?.label) >= 0;
            if (input?.label && !defaultInputs?.includes(input?.label) && !hasEvent) {
              inputEvents.push({
                label      : input?.label,
                isSelected : false,
                color      : null
              });
            }
          } catch (error) {
            error(error);
          }
        });
      });
      const inputEventsSorted = sortby(inputEvents, [({label}) => label?.toUpperCase()]);
      inputEventsSorted?.forEach((input, index) => {
        input.isSelected = isInputSelected(inputsEvents, input);
        input.color      = color[index] || getHistoryColors().brown;
      });
      // Add the Default Value
      inputEventsSorted.push({
        label      : None,
        isSelected : true,
        color      : null
      });
      // Add the Mixed Selected
      inputEventsSorted.push(getInputMixedSelection(inputEventsSorted, MAX_INPUT_SELECTED));
      const allInputEvents = isTracking2 ? unionBy(inputEventsSorted, inputsV2, 'label') : inputEventsSorted;
      dispatch(storeInputEvents(allInputEvents));
    } else {
      dispatch(storeInputEvents([]));
    }
  };
};

/**
 * getAlignedRoutes method checkes cached response for
 * given requestBody and make a request to HERE api if not stored.
 * It will update the redux store to cache the response from HERE api
 * for later usage.
 * It returns an updated routeHistory only if needed.
 *
 * ```typescript
 * import {getAlignedRoutes} from '@/store/actions/tracking';
 *
 * const response = await dispatch(updateAssetTrackingHistory(assets, requestBody));
 * ...
 *
 * ```
 *
 */
export const getAlignedRoutes = async(assetsMap, routeHistory) => {
  const {
    ALIGNED_ROUTES_CSV_HEADERS,
    ALIGNED_ROUTES_MAX_DISTANCE
  } = ALIGNED_ROUTES_CONFIG;
  const requestMap = {};
  const state      = cloneDeep(routeHistory);

  // loop through the assetsMap and check if request with same coordinates is
  // cached in the redux store, if not, assign request to the requestMap
  for (const key in assetsMap) {
    const requestBody = createCSV(assetsMap[key], ALIGNED_ROUTES_CSV_HEADERS);
    const result      = requestBody? routeHistory[requestBody] : null;
    if (!result) {
      requestMap[key] = assetsMap[key];
    }
  }
  try {
    if (!isEmpty(requestMap)) {
      const responses = (await massRouteMatching(requestMap) as any).map(({value}) => value);
      if (responses) {
        for (const asset of responses) {
          const {response, assetId} = asset;
          const nodes  = await createAlignedNodes(response, assetsMap[assetId], ALIGNED_ROUTES_MAX_DISTANCE);
          const routes       = createPathArray(response, nodes);
          const requestBody  = createCSV(assetsMap[assetId], ALIGNED_ROUTES_CSV_HEADERS);
          state[requestBody] = {nodes, routes};
        }
        return state;
      }
    }
  } catch (err) {
    error(err);
  }
};

/**
 * loadAssetHistory
 *
 * @param hours is the hours to subsctracted from
 * current date-time
 *
 * loadAssetHistory method retrieves history assets
 * from the HVS Services endpoint
 */
export const loadAssetHistory = (hours: number = 2, isUpdate: boolean = false, force: boolean = false) => {
  return async(dispatch, getState) => {
    let assets = [];
    !isUpdate && dispatch({type: GET_ASSETS_HISTORY_REQUEST, loading: true});
    try {
      const groupIds = getCheckedAccountIds(getState);
      const {
        tracking: {
          history: {
            assets : assetsHistory
          },
          filters: {
            assetTypes,
            assetCategories,
            geoFences,
            routes,
            assetStatus,
            assets: {
              selected: assetsSelected
            }
          },
          maps : {
            layers
          },
          trackingHistory: {
            routeHistory
          }
        }
      } = getState();
      if (layers?.includes(HISTORY_LAYER)) {
        const assetTypesSelected      = [...getFilterRequestParams(assetTypes.selected)];
        const assetCategoriesSelected = [...getFilterRequestParams(assetCategories.selected)];
        const geoFencesSelected       = [...getFilterRequestParams(geoFences.selected)];
        const routesSelected          = [...getFilterRequestParams(routes.selected)];
        const assetStatusSelected     = [...getFilterRequestParams(assetStatus.selected)];

        const params = {
          groups     : groupIds,
          assets     : getFilteredAssets(assetsSelected, groupIds),
          start      : null,
          end        : null,
          types      : assetTypesSelected,
          categories : assetCategoriesSelected,
          geoFences  : geoFencesSelected,
          routes     : routesSelected,
          status     : assetStatusSelected
        };

        const reponse = await getAssetCount({
          groups          : groupIds,
          assets          : getFilteredAssets(assetsSelected, groupIds),
          assetTypes      : assetTypesSelected,
          assetCategories : assetCategoriesSelected,
          geoFences       : geoFencesSelected,
          routes          : routesSelected,
          assetStatus     : assetStatusSelected
        });
        const valid = !(reponse.count > get('maxHVSAssetDays'));
        if (valid) {
          if (((!assetsHistory.length || force) && !isUpdate) || (!assetsHistory?.length && isUpdate)) {
            const dates  = getDateFromToday(hours);
            params.start = dates?.fromDate;
            params.end   = dates?.toDate;
            assets       = await getAssetHistory(params);
            if (isRouteMatchingEnabled(reponse.count)) {
              const assetsMap = createAssetsMap(assets);
              const state = await getAlignedRoutes(assetsMap, routeHistory);
              if (state) {
                dispatch(setTrackingHistoryReport(state));
              }
            }
            dispatch(setIsHistoryRefresh(false));
            dispatch({type: GET_ASSETS_HISTORY_RESPONSE, assets});
            dispatch({type: SET_ASSETS_HISTORY_ERROR, showError: false, assetCount: reponse.count});
            dispatch(setAssetHistoryInterval(hours));
            dispatch(setAssetsHistoryUpdatesLive(true));
            dispatch(setHistoryInputEvents(assets));
          } else {
            params.start = new Date(getLatestTime(assetsHistory, 'eventTime'));
            assets       = await getAssetHistory(params);
            if (isRouteMatchingEnabled(reponse.count)) {
              const assetsMap = createAssetsMap(assets, assetsHistory);
              const state = await getAlignedRoutes(assetsMap, routeHistory);
              if (state) {
                dispatch(setTrackingHistoryReport(state));
              }
            }
            if (assets.length) {
              dispatch(setIsHistoryRefresh(true));
              dispatch({type: GET_ASSETS_HISTORY_RESPONSE, assets: [...assetsHistory, ...assets]});
            } else {
              dispatch({type: GET_ASSETS_HISTORY_REQUEST, loading: false});
            }
          }
        } else {
          dispatch({type: SET_ASSETS_HISTORY_ERROR, showError: true, assetCount: reponse.count});
          dispatch(setAssetHistoryInterval(hours));
          dispatch(setAssetsHistoryUpdatesLive(false));
        }
      }
    } catch (error) {
      dispatch({type: GET_ASSETS_HISTORY_ERROR, loading: false});
    }
  };
};

/**
 * setAssetsHistoryUpdatesLive
 *
 * @param status is the status of the automatically updates
 * active/true  or inactive/false
 */
export const setAssetsHistoryUpdatesLive = (status) => {
  return {type: SET_ASSETS_HISTORY_UPDATES_LIVE, status};
};

/**
 * setIsHistoryRefresh
 *
 * @param refresh represent if it is from automatically updates
 */
export const setIsHistoryRefresh = (refresh) => {
  return {type: SET_HISTORY_REFRESH, refresh};
};

// Set isLayerListUpdate state
export const setIsLayerListUpdate = (value: boolean) => {
  return {type: SET_IS_LAYER_LIST_UPDATE, value};
};

// Set Asset History Interval in Hours
export const setAssetHistoryInterval = (hours: number) => {
  return {type: SET_ASSETS_HISTORY_LIVE_INTERVAL, hours};
};

/**
 * setOperationalLayersVisibility
 *
 * Defines a utility to set Operational Layers visibility on the map
 */
const setOperationalLayersVisibility = (visibleLayers: string[] = []) => {
  return async(dispatch, getState) => {
    const {
      tracking: {
        maps
      }
    } = getState();
    const titles  = maps.operationalLayers?.map(({title}) => title);
    const updated = maps.layers?.filter(item => !titles?.includes(item));
    dispatch(setTrackingMapLayers(updated?.concat(visibleLayers)));
  };
};

/**
 * @function initializeOperationalLayers
 */
export const initializeOperationalLayers = (map: __esri.Map) => {
  return async(dispatch, getState) => {
    const {
      tracking: {
        maps : {
          operationalLayers : currentState
        }
      }
    } = getState();

    let operationalLayers = augmentOperationalLayers(map).reverse();
    // Preserve the state of Operational Layers
    const currentIds = currentState?.map(({id}) => id);
    const updatedIds = operationalLayers?.map(({id}) => id);
    if (currentState?.length && isDeepEqual(updatedIds, currentIds)) {
      operationalLayers = unionBy(currentState, operationalLayers, 'id');
    } else {
      const visibleLayers = operationalLayers
        ?.filter(({visibility}) => visibility)
        ?.map(({title}) => title);
      // Set Layers visibility
      dispatch(setOperationalLayersVisibility(visibleLayers));
    }
    dispatch({
      type              : INITIALIZE_OPERATIONAL_LAYERS,
      operationalLayers : operationalLayers
    });
  };
};

/**
 * @function setOperationalLayersOpacity
 */
export const setOperationalLayersOpacity = (updated) => {
  return {type: SET_OPERATIONAL_LAYER_OPACITY, updated};
};

/**
 * @function setOperationalLayerVisibility
 */
export const setOperationalLayerVisibility = (updated) => {
  return {type: SET_OPERATIONAL_LAYER_VISIBILITY, updated};
};

export const setOperationalSublayerVisibility = (updated) => (
  {type: SET_OPERATIONAL_SUBLAYER_VISIBILITY, updated}
);

export const setSavedOperationalLayers = (updated) => (
  {type: SET_OPERATIONAL_LAYERS, updated}
);

/**
 * @function resetTracking
 */
export const resetTracking = () => {
  return async(dispatch) => {
    dispatch({type: RESET_TRACKING});
    dispatch(applyAssetHistoryDateFilter({
      selectedRadio : DateSelectorValues.Live,
      fromDate      : null,
      endDate       : null
    }) as any);
  };
};

// Defines a utility to reset selected asset
export const resetSelectedAsset = isMapSelection => {
  return async(dispatch) => {
    dispatch(setSelectedAsset({asset: null, coordinates: isMapSelection}));
    dispatch({type: SET_ASSET_DETAIL_VIEW, value: false});
  };
};

type LatestAssetData = {
  selectedAsset: Devices;
  includeTime: boolean;
  returnAsset?: boolean;
  includeSciDevice?: boolean;
};

// Defines a utility to return an asset by query ArcGIS endpoint
export const getLatestAssetData = async({
  selectedAsset,
  includeTime,
  returnAsset = false,
  includeSciDevice = false
}: LatestAssetData) => {
  const selectedItem = getItemValue(selectedAsset);
  if (includeSciDevice && isSCIDevice(selectedItem)) {
    const device      = await getSCDevices({devices: [selectedItem?.deviceId]});
    const firstDevice = getItemValue(parseSciCollection(device)[0]);
    return firstDevice;
  }

  if (selectedAsset?.isCVSAsset?.()) {
    const time   = includeTime ? new Date(selectedItem?.dbWriteTime).toISOString() : null;
    const assets = await getCVSAssets({
      assetId : selectedItem?.assetId,
      filter  : time ? getDbWriteTimeFilter(time) : []
    }, includeSciDevice);
    if (assets?.length) {
      return returnAsset ? assets?.[0] : Devices.getAssetsFromCollection(assets)[0];
    }
  }
  return returnAsset ? getItemValue(selectedAsset) : selectedAsset;
};

// Defines a utility to retrieve the asset entity that combines CVS + CTC + Device data.
const getAssetUpdated = async(
  largeFleetAssets: Devices[],
  selectedAsset: Devices | BaseAsset,
  isLargeFleetMode: boolean
): Promise<any> => {
  let largeFleetAsset = null;
  let device          = null;

  if (isLargeFleetMode) {
    const filteredAsset = largeFleetAssets.find(device =>
      device?.getAssetValue('assetId') === selectedAsset?.id);
    largeFleetAsset = getItemValue(filteredAsset) ?? {};
  }
  const ctcAsset = !selectedAsset?.isScDevice ? await cacheOnFetch({
    queryKey : createKey(QueryKey.GET_ASSET, selectedAsset?.id),
    queryFn  : async() => await getAsset(selectedAsset?.id)
  }) : {};
  const assignedDevice = ctcAsset?.devices?.[0] || null;
  if (assignedDevice) {
    const deviceId = entityId(assignedDevice);
    const devices = await cacheOnFetch({
      queryKey : createKey(QueryKey.DEVICE_GET, deviceId),
      queryFn  : async() => await getDevice(deviceId)
    });
    device        = devices?.[0] || null;
  }
  const mergedAsset  = {...selectedAsset, ...largeFleetAsset, ...ctcAsset, device};
  const assetUpdated = Devices.getAssetsFromCollection([mergedAsset])?.[0] || null;
  return assetUpdated;
};

// Defines a utility to update selectedAsset state to latest updated
const verifySelectedAsset = (selectedAsset: Devices, includeTime: boolean = false, isMapSelection?: boolean) => {
  return async(dispatch) => {
    if (selectedAsset) {
      const asset = await getLatestAssetData({selectedAsset, includeTime, returnAsset: true});
      dispatch(updateSelectedAsset({selectedAsset, assets: [asset], isMapSelection}));
    }
  };
};

/**
 * Defines a utility for update the selected asset
 */
export const updateSelectedAsset = ({
  assets,
  panMapOnly,
  selectedAsset,
  isMapSelection,
  tracking2Page = false
}: UpdateSelectedAsset) => {
  return async(dispatch) => {
    if (selectedAsset && assets?.length) {
      const selectedAssetId = getAssetId(selectedAsset);
      let updated = assets.find(({assetId}) => assetId === selectedAssetId);
      let parsedAsset;
      if (updated) {
        if (isSCIDevice(updated) && tracking2Page) {
          const device = await getSCDevices({devices: [updated?.deviceId]});
          parsedAsset  = parseSciCollection(device)[0];
        } else {
          if (tracking2Page) {
            [updated] = await getCVSAssets({
              assetId : selectedAssetId
            });
          }
          if (updated) {
            const {isScDevice, assetId} = updated || {};
            const assetUpdated          = !isScDevice && assetId ? await cacheOnFetch({
              queryKey : createKey(QueryKey.GET_ASSET, assetId),
              queryFn  : async() => await getAsset(assetId)
            }) : {};
            const associatedDevice      = (assetUpdated as Asset)?.devices?.[0];
            const deviceId              = entityId(associatedDevice);
            const device                = associatedDevice ? await cacheOnFetch({
              queryKey : createKey(QueryKey.DEVICE_GET, deviceId),
              queryFn  : async() => await getDevice(deviceId)
            }) : null;
            updated     = {...updated, ...assetUpdated, device: device?.[0]};
            parsedAsset = Devices.getAssetsFromCollection([updated])[0];
          }
        }
        dispatch({
          type  : SET_SELECTED_ASSET,
          asset : parsedAsset,
          isMapSelection,
          panMapOnly
        });
      }
    }
  };
};

/**
 * @function getAssetsUpdates
 *
 * The getAssetsUpdates get the Assets updated based on the 'dbWriteTime' field and
 * update the asset state and selectedAsset as needed
 */
export const getAssetsUpdates = (time: string) => {
  return async(dispatch, getState) => {
    let latestTime = null;
    const groupIds = getCheckedAccountIds(getState);
    const {
      nav: {
        currentPage
      },
      tracking:
      {
        assets: {
          assets,
          updates: assetsUpdates,
          selectedAsset
        },
        filters: {
          assetTypes,
          assetCategories,
          geoFences,
          routes,
          assetStatus,
          assets : {
            selected: assetsSelected
          },
          boundary
        },
        grid: {
          filteredAssetIds
        },
        findNearby: {
          isActive
        }
      },
      globalGroup: {
        globalGroupHierarchy
      }
    } = getState();

    if (!isCurrentPage(currentPage, Route.TRACKING2)) {
      if (filteredAssetIds?.length || !time || isActive) {
        const allAssets = [...assets, ...assetsUpdates];
        latestTime      = new Date(getLatestTime(allAssets, DATETIME_PROPERTY)).toISOString();
      }
      const assetsAllowed = getItemAllowed(assetsSelected, globalGroupHierarchy);
      const updates = await getCVSAssets({
        groups          : groupIds,
        assets          : getFilteredAssets(assetsAllowed, groupIds),
        assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
        assetCategories : [...getFilterRequestParams(assetCategories.selected)],
        geoFences       : [...getFilterRequestParams(geoFences.selected)],
        routes          : [...getFilterRequestParams(routes.selected)],
        assetStatus     : [...getFilterRequestParams(assetStatus.selected)],
        filter          : getDbWriteTimeFilter(latestTime || time),
        geometry        : boundary
      });

      /* Getting the latest state here, because this process is running
      *  in the background and we want to make sure that when Suite
      *  switches to Large Fleet Mode the calls won't update the assets
      */
      const {isLargeFleetMode} = getState().tracking.fleetMode;
      if (!isLargeFleetMode && updates?.length) {
        dispatch({
          type          : TRACKING_ASSETS_UPDATES,
          updates       : Devices.getAssetsFromCollection(updates),
          isAutoRefresh : true
        });
        dispatch(updateSelectedAsset({selectedAsset, assets: updates, isMapSelection: true}));
      }
    }
  };
};

/**
 * @function setMapInitialized
 */
export const setMapInitialized = () => ({
  type : MAP_INITIALIZED
});

const searchPriority = {
  isAsset    : 0,
  isDriver   : 1,
  isGeofence : 2,
  isVehicle  : 3,
  isPlace    : 4,
  isFcDevice : 5
};

const getSearchResultPriority = (item) => {
  return searchPriority[Object.keys(searchPriority).find(f => item[f]) || 5];
};

const preOrderSearchResults = (key: string) => {
  const searchKeys = splitBySpecialCharacters(key, true);
  const getMatchCount = (name: string) => {
    const nameInLowerCase = name.toLowerCase();
    return searchKeys.reduce((acc: number, key: string) => {
      nameInLowerCase.indexOf(key.toLowerCase()) !== -1 && (acc += 1);
      return acc;
    }, 0);
  };
  return (a: GenericObject, b: GenericObject) => {
    const matchA = getMatchCount(a.name);
    const matchB = getMatchCount(b.name);
    if (matchA === matchB) {
      const priorityA = getSearchResultPriority(a);
      const priorityB = getSearchResultPriority(b);
      // sorting items by priority, eg.: Asset, Geofence, Location...
      if (priorityA > priorityB) return 1;
      if (priorityA < priorityB) return -1;
    }
    // sorting items by best match...
    if (matchA < matchB) return 1;
    if (matchA > matchB) return -1;
    return 0;
  };
};

/**
 * @search
 *
 * Executes a tracking search which performs a search against CVS services for
 * assets, drivers, geofences, and places.
 */
// whether this term is still the one most recently searched ...
const isLatestSearchTerm = (getState: Function, searchTerm: string): boolean => (
  getState()?.tracking?.search?.searchTerm === searchTerm
);

export const search = (searchTerm: string, preOrder: boolean = false) => {
  return async(dispatch, getState) => {
    dispatch({type: TRACKING_SEARCH_REQUEST, searchTerm});
    const searchTypes = [
      SearchType.asset,
      SearchType.driver,
      SearchType.geofence,
      SearchType.place,
      SearchType.fcdevice
    ];
    const {
      nav: {
        currentPage
      },
      tracking : {
        maps: {
          layers,
          view
        }
      }
    } = getState();
    
    const isTrackingBeta = isCurrentPage(currentPage, Route.TRACKING2);

    if (Features.hasSCIAccess() && !isTrackingBeta) {
      searchTypes.push(SearchType.scdevice);
    }
    try {
      const {latitude, longitude} = view.center;
      const results = await globalSearch({
        accountIds         : getAllAccountIds(getState),
        geofenceAccountIds : getGeofenceAccountIds(getState),
        consolidate        : true,
        useCVS             : true,
        useHereApi         : true,
        showMatchLevel     : true,
        center             : [latitude, longitude],
        limitResults       : SEARCH_LIMIT_RESULTS,
        types              : searchTypes,
        includeSciDevice   : isTrackingBeta,
        // the current theme finds a geofence whatever the case typed ...
        ignoreCase         : isCurrentTheme(),
        searchTerm
      });
      if (preOrder) {
        const formattedSearchTerm = formatSearchTerm(searchTerm);
        results.consolidated = results.consolidated.sort(preOrderSearchResults(formattedSearchTerm));
      }
      // a slower response for an earlier term (e.g. '9' while '9029' is
      // typed) arrives after the newer request - only the latest term's
      // results are kept ...
      if (isLatestSearchTerm(getState, searchTerm)) {
        dispatch({type: TRACKING_SEARCH_RESPONSE, results});
      }
    } catch (err) {
      error(err);
      if (isLatestSearchTerm(getState, searchTerm)) {
        dispatch({type: TRACKING_SEARCH_ERROR, error: err});
      }
    }
  };
};

export const clearSelectedTrackingSearchResult = () => {
  return async(dispatch, getState) => {
    dispatch({type: SET_SELECTED_TRACKING_SEARCH_RESULT, result: null});
  };
};

/**
 * Invoked when the user selects a search result from the results list.
 */
export const setSelectedTrackingSearchResult = (result: GlobalSearchResult, useCtc?: boolean): GenericObject => {
  return async(dispatch, getState) => {
    if (result) {
      const {
        nav: {
          currentPage
        }
      } = getState();
      const isTrackingBeta = isCurrentPage(currentPage, Route.TRACKING2);
      const {type} = result;
      let validated;
      let historyResult;
      let assetAsDevice;
      if (type === SearchType.scdevice) {
        validated = {item: result?.item, isScDevice: true};
        assetAsDevice = Devices.getDevicesFromCollection([{attributes: validated?.item}])[0];
        const deviceValidated = {
          hasList    : false,
          isAsset    : false,
          isDriver   : false,
          isFcDevice : false,
          isGeofence : false,
          isPlace    : false,
          isVehicle  : false
        };
        historyResult = {
          ...validated,
          ...deviceValidated,
          item        : assetAsDevice,
          type        : AssetType.ScDevice,
          name        : assetAsDevice.getAssetValue?.('deviceName'),
          displayName : result.name
        };
      } else {
        const {
          tracking : {
            maps: {
              view
            }
          }
        } = getState();
        const {latitude, longitude} = view?.center || {};
        validated = await revalidate({
          accountIds         : getAllAccountIds(getState),
          geofenceAccountIds : getGeofenceAccountIds(getState),
          useCVS             : true,
          useHereApi         : true,
          center             : latitude && latitude ? [latitude, longitude] : null,
          includeSciDevice   : isTrackingBeta,
          result
        });
        if (useCtc && type === SearchType.asset && validated?.item) {
          const {
            tracking: {
              fleetMode: {
                isLargeFleetMode,
                largeFleetAssetList: {
                  assets: largeFleetAssets
                }
              }
            }
          } = getState();
          assetAsDevice = await getAssetUpdated(largeFleetAssets, validated.item, isLargeFleetMode);
        } else {
          assetAsDevice = Devices.getAssetsFromCollection([validated?.item])[0];
        }
        historyResult = {
          ...validated,
          item        : assetAsDevice,
          displayName : result.name
        };
      }
      batch(() => {
        dispatch({type: SET_SELECTED_TRACKING_SEARCH_RESULT, result: historyResult});
        dispatch(setSideSheetStatus({
          desktopSideSheetOpen : true
        }));
      });
      const {item, isAsset, isDriver, isFcDevice, isScDevice} = validated || {};
      if (item && (isAsset || isDriver || isFcDevice || isScDevice)) {
        dispatch(setHoveredAsset(assetAsDevice, null));
        const assetUpdated = await getLatestAssetData({
          selectedAsset    : assetAsDevice,
          includeTime      : false,
          returnAsset      : false,
          includeSciDevice : isTrackingBeta
        });
        dispatch(setAssetDetailView({
          value            : true,
          forcePanelToOpen : true,
          fromTools        : false,
          selectedItem     : assetUpdated
        }));
      }
      if (validated?.item && validated?.isScDevice) {
        dispatch({type: SET_SELECTED_ASSET, asset: assetAsDevice});
      }
    }
  };
};
/**
 * @function setHoveredAsset
 */
export const setHoveredAsset = (asset: Devices, source: string) => {
  return {type: SET_HOVERED_ASSET, asset, source};
};

/**
 * @function setSelectedAsset
 */
export const setSelectedAsset = ({
  asset,
  coordinates,
  useCtc,
  resetTool = false
}: SelectedAssetProps) => {
  return async(dispatch, getState) => {
    let assetAsDevice = asset;
    const {
      tracking: {
        fleetMode: {
          isLargeFleetMode,
          largeFleetAssetList: {
            assets: largeFleetAssets
          }
        }
      }
    } = getState();
    if (assetAsDevice && useCtc) {
      const selectedAsset = getItemValue(assetAsDevice);
      assetAsDevice       = await getAssetUpdated(largeFleetAssets, selectedAsset, isLargeFleetMode);
    }
    dispatch({type: SET_SELECTED_ASSET, asset: assetAsDevice, isMapSelection: coordinates});
    if (resetTool) {
      dispatch(setTool(null));
      dispatch(setToolDetailView(false));
    }
  };
};

export const setLastSelectedAsset = (asset: Devices) => ({
  type : SET_LAST_SELECTED_ASSET, asset
});

export const setPanelTopPosition = (position: number) => ({
  type : SET_PANEL_TOP_POSITION, position
});

/**
 * Shows or hides the side sheet's asset details loader - e.g. right as an
 * asset card is clicked, before the details themselves are put together.
 *
 * ```typescript
 *
 *   dispatch(setAssetDetailsLoading(true));
 *
 * ```
 *
 * @param   value `true` to show the loader, `false` to hide it.
 * @returns       The action.
 */
export const setAssetDetailsLoading = (value: boolean) => ({
  type : SET_ASSET_DETAILS_LOADING, value
});

export const setAssetSelectionType = (value: AssetSelectionType | null) => {
  return (dispatch) => {
    dispatch({type: SET_ASSET_SELECTION_TYPE, value});
  };
};

/**
 * @function setToolPanel
 */
export const setToolPanel = (newState: {assetId: string | null, open: boolean}[]) => {
  return {type: SET_TOOL_PANEL_OPEN, newState};
};

export const setAssignedAssetsPanels = (openPanels: string = null) => {
  return {type: SET_ASSIGNED_ASSETS_PANEL_OPEN, openPanels};
};

/**
 * @function setGeoFencePanelOpen
 */
export const setGeoFencePanelOpen = (geoFencePanelOpen: boolean) => {
  return {type: SET_GEOFENCE_PANEL_OPEN, geoFencePanelOpen};
};

/**
 * @function getSelectedAssetTools
 *
 * Retrieves the tools associated with the given asset, as well as each tools
 * current visibility.
 *
 * By default, the returned Assets (Tools) will have 'tagReport'  property added.
 * The presence of the 'tagReport' property indicates that the tool is visible to
 * the asset, and the absence of the property means that tool is not visible.
 *
 * To omit retrieving the visibility of each tool (which incurs an additional
 * service invocation), simply pass the 'includeVisibility' argument as false.
 */
export const getSelectedAssetTools = (asset: Asset, includeVisibility = true) => {
  return async(dispatch, getState) => {
    const {
      globalGroup : {
        currentAccount
      }
    } = getState();
    dispatch({type: GET_ASSET_TOOLS_REQUEST, asset});
    let tools;
    try {
      const currentAsset = getItemValue(asset);
      if (isScDevice(asset)) {
        const ESNs = getDevicesByESN(asset);
        // an SC device with no tag reports has no serial numbers to search
        // for - the empty `(serialNumber:)` query only came back a 400 ...
        if (ESNs.join('').trim()) {
          const {devices} = await loadDevicesBySmartSearch({
            queryString : `(serialNumber:${ESNs})`,
            accountId   : asset?.accountId
          });
          tools = devices;
        }
      } else {
        tools = await getAssociatedAssets(currentAsset, includeVisibility);
      }
      const assetId = currentAsset.id;
      const toolsData = tools?.length ? tools : null;
      dispatch({type: GET_ASSET_TOOLS_RESPONSE, assetId: assetId, tools: toolsData});
    } catch (err) {
      dispatch({type: GET_ASSET_TOOLS_ERROR, err});
      error(err);
    }
  };
};

/**
 * @function getDisconnectedToolReport
 *
 * Retrieves the most recent tagReport for an individual tool that did
 * not include a tagReport from LMU Gateway events.
 */
export const getDisconnectedToolReport = (asset: ToolAssetType, msgType = 'TAG_REPORT') => {
  let deviceId;
  const {devices} = asset;
  if (devices) {
    const [device] = devices;
    deviceId =  entityId(device);
  } else {
    deviceId = asset?.id;
  }
  return async dispatch => {
    try {
      const toolReport = await getDeviceReport({
        deviceId,
        useEsn    : false,
        timeRange : 7,
        msgType
      });
      // the call to results service limits the results to 1 so we can
      // safely grab the first element in the results array
      const tagReport = toolReport.results.length ? toolReport.results[0] : null;
      dispatch({type: SET_DISCONNECTED_TOOL_TAG_REPORT, tagReport});
    } catch (err) {
      dispatch({type: SET_DISCONNECTED_TOOL_ERROR, err});
      error(err);
    }
  };
};

/**
 * @function setDisconnectedToolReport
 */
export const setDisconnectedToolReport = (tagReport: null | TagReportType) => {
  return {type: SET_DISCONNECTED_TOOL_TAG_REPORT, tagReport};
};

/**
 * @function findNearest
 *
 * Retrieves all assets that are within the given radius of the provided asset's geodata.
 */
export const findNearby = ({latitude, longitude, miles, label, findType = null}: FindNearbyRequestArgs) => {
  return async(dispatch, getState) => {
    const {
      nav: {
        sideSheet: {
          desktopSideSheetOpen
        }
      },
      tracking: {
        sideSheetView,
        filters: {
          assetTypes,
          assetCategories,
          geoFences,
          routes,
          assetStatus,
          assets: {
            selected: assetsSelected
          }
        },
        findNearby: {
          type
        }
      }
    } = getState();

    isBottomSheetTableView(sideSheetView) && dispatch(setSideSheetStatus({desktopSideSheetOpen: false}));
    const groupIds = getCheckedAccountIds(getState);
    dispatch({type: FIND_NEARBY_REQUEST, miles: miles ?? 10, label, findType});
    try {
      const params: CVSQueryArgs = {
        groups          : groupIds,
        assets          : getFilteredAssets(assetsSelected, groupIds),
        assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
        assetCategories : [...getFilterRequestParams(assetCategories.selected)],
        geoFences       : [...getFilterRequestParams(geoFences.selected)],
        routes          : [...getFilterRequestParams(routes.selected)],
        assetStatus     : [...getFilterRequestParams(assetStatus.selected)]
      };
      const results    = await findNearest({latitude, longitude, parameters: params, miles: miles ?? 10});
      const assetCoordinates = {latitude, longitude};
      const isUomMiles       = isImperial(getUserUnit());
      results?.forEach((asset) => {
        const {longitude, latitude} = asset;
        const distanceValue = getDistanceByCoordinates(
          assetCoordinates,
          {longitude, latitude}
        );
        asset.distance = Math.round((isUomMiles ? mToMi(distanceValue) : mToKm(distanceValue)) * 10) / 10;
      });
      const sortedAssets = sortby(results, ['distance']);
      if (type === FindNearbyType.location) {
        dispatch(setSelectedAsset({asset: null}));
      }
      dispatch(setTool(null));
      dispatch({type: FIND_NEARBY_RESPONSE, results: Devices.getAssetsFromCollection(sortedAssets)});
      if (!desktopSideSheetOpen) {
        dispatch(setSideSheetStatus({
          activeSideSheet      : getRoute(Route.TRACKING2),
          desktopSideSheetOpen : true
        }));
      }
      dispatch(applyBoundaryFilter([]));
      dispatch({type: SET_FIND_NEARBY_ASSET, value: null});
    } catch (error) {
      dispatch({type: FIND_NEARBY_ERROR, error});
    }
  };
};

/**
 * @exitFindNearby
 */
export const exitFindNearby = (refreshAssetList: boolean = false, omitExitFindNearbyV2 = false) => {
  return async(dispatch, getState) => {
    const {
      tracking : {
        sideSheetView,
        assets: {
          assets,
          selectedAsset
        },
        supplyChainDevices: {
          devices
        },
        findNearby: {
          isActive
        },
        fleetMode: {
          isLargeFleetMode
        }
      }
    } = getState();

    if (isActive) {
      if (!isLargeFleetMode) {
        assets?.length && dispatch(getAssetsUpdates(null));
        devices?.length && dispatch(loadSCDevicesUpdates(null));
      } else if (!isSheetTableView(sideSheetView) && !selectedAsset && refreshAssetList) {
        dispatch(getCVSPaginatedAssets({startRow: 0, forceUpdate: true}));
      }
    }
    dispatch({type: EXIT_FIND_NEARBY});
    if (!omitExitFindNearbyV2) {
      dispatch({type: SET_FIND_NEARBY_PANEL_ACTIVE, isActive: false});
    }
  };
};

/**
 * @setFindNearbyPopup
 */
export const setFindNearbyPopup = (status) => {
  return {
    type : SET_FIND_NEARBY_POPUP_STATE, status
  };
};

/**
 * @setFindNearbyToggle
 */
export const setFindNearbyToggle = () => ({
  type : SET_FIND_NEARBY_TOGGLE
});

/**
 * @setFindNearbyAsset
 */
export const setFindNearbyAsset = (value) => ({
  type : SET_FIND_NEARBY_ASSET, value
});

/**
 * @setFindNearbyGeofence
 */
export const setFindNearbyGeofence = (value) => ({
  type : SET_FIND_NEARBY_GEOFENCE, value
});

/**
 * @setLocationFindNearby
 */
export const setLocationFindNearby = (value) => ({
  type : SET_LOCATION_FIND_NEARBY, value
});

/**
 *
 * @setFindButtonClickStatus
 */
export const setFindButtonClickStatus = (status) => {
  return {
    type : SET_FIND_BUTTON_STATE, status
  };
};

/**
 * @reviewNotification
 */
export const reviewNotification = (notification: Notification) => {
  return async(dispatch, getState) => {
    const {
      tracking : {
        assets : {
          assets
        },
        fleetMode : {
          isLargeFleetMode
        },
        filters: {
          assetTypes,
          assetCategories,
          geoFences,
          routes,
          assetStatus
        }
      }
    } = getState();

    const getAsset = async() => {
      if (!isLargeFleetMode && !(isDeviceAlert(notification))) {
        const selectedAsset = assets?.find((assetItem) => {
          const asset = getItemValue(assetItem);
          return `${asset.assetId}` === `${notificationAssetId}`;
        });
        if (!isNil(selectedAsset)) {
          return selectedAsset;
        }
      }
      const params = {
        assetId         : parseInt(notificationAssetId),
        assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
        assetCategories : [...getFilterRequestParams(assetCategories.selected)],
        geoFences       : [...getFilterRequestParams(geoFences.selected)],
        routes          : [...getFilterRequestParams(routes.selected)],
        assetStatus     : [...getFilterRequestParams(assetStatus.selected)]
      } as CVSQueryArgs;
      return await getCVSAssets(params);
    };

    let notificationAssetId = notification?.device?.href.split('/').pop();
    if (!(isDeviceAlert(notification))) {
      notificationAssetId = notification?.asset?.href.split('/').pop();
    }
    dispatch(setTrackingMode(TrackingMode.Live));
    dispatch(initializeTracking());
    let asset: Devices = null;
    if (!isLargeFleetMode && !(isDeviceAlert(notification))) {
      const assets        = await getAsset();
      const selectedAsset = assets?.[0] || assets;
      if (selectedAsset instanceof Devices) {
        asset = selectedAsset;
      } else {
        [asset] = Devices.getAssetsFromCollection([selectedAsset]);
      }
      dispatch(setSelectedAsset({asset}));
    } else {
      let assetsAsDevices;
      if (!(isDeviceAlert(notification))) {
        const assets = await getAsset();
        [assetsAsDevices] = Devices.getAssetsFromCollection(assets);
      } else {
        const result = await getSCDevices({devices: [notificationAssetId]} as CVSQueryArgs);
        assetsAsDevices = parseSciCollection(result)[0];
      }
      assetsAsDevices && dispatch(setSelectedAsset({asset: assetsAsDevices}));
      asset = assetsAsDevices;
    }
    dispatch(setSideSheetView(SideSheetView.detailed));
    dispatch(setAssetDetailView({
      value            : true,
      forcePanelToOpen : false,
      fromTools        : false,
      selectedItem     : asset
    }));
    dispatch(setSideSheetStatus({
      activeSideSheet      : `/${Route.TRACKING2}`,
      desktopSideSheetOpen : true
    }));
  };
};

/**
 * Gets the associated CTC (Connect) assets for the given asset id and stores the results in the redux store.
 */
export const getAssociatedAssetsForAsset = ({
  assetId,
  includeVisibility = true,
  includeIONTags = true,
  trackingV2 = false,
  associatedAssetId
}) => {
  return async(dispatch, getState) => {
    const {
      tracking: {
        assets: {
          assets
        },
        fleetMode: {
          isLargeFleetMode,
          largeFleetAssetList: {
            assets: largeFleetAssets
          }
        }
      }
    } = getState();
    dispatch({type: GET_ASSOCIATED_ASSETS_REQUEST});
    try {
      const associatedAsset      = await getAsset(assetId);
      const associatedAssets     = await getAssociatedAssets(associatedAsset, includeVisibility, includeIONTags);
      const associatedAssetsData = !associatedAssets.length ? null : associatedAssets;
      const assetsList           = isLargeFleetMode ? largeFleetAssets : assets;
      const item = associatedAssetsData?.filter(({id}) => id === associatedAssetId)[0];
      if (associatedAssetsData) {
        if (item?.assetType === AssetType.Tool) {
          let selectedAsset = getAssetById(assetsList, assetId)[0];
          if (trackingV2) {
            const asset = await getCVSAssets({
              assetId : assetId
            });
            selectedAsset = Devices.getAssetsFromCollection(asset)[0];
          }
          setToolDetailView(true);
          dispatch({type: SET_SELECTED_ASSET, asset: selectedAsset});
          dispatch(setTool(item));
          dispatch({type: GET_ASSOCIATED_ASSETS_ERROR, error: null});
        } else {
          let associatedAssetInfo = null;
          if (trackingV2) {
            associatedAssetInfo = await getCVSAssets({
              assetId : associatedAssetId
            });
          }
          if (trackingV2 && !associatedAssetInfo?.length) {
            const devices = await getSCDevices({assets: [associatedAssetId]});
            associatedAssetInfo = parseSciCollection(devices);
          }
          if (!associatedAssetInfo?.length) {
            associatedAssetInfo = await getAsset(associatedAssetId);
          }
          batch(() => {
            let assetInfo = null;
            if (trackingV2 && associatedAssetInfo?.[0] instanceof Devices) {
              assetInfo = associatedAssetInfo[0];
            } else {
              assetInfo = getAssetById(assetsList, associatedAssetId)[0] ??
              Devices.getAssetsFromCollection([associatedAssetInfo[0] ?? associatedAssetInfo])[0];
            }
            dispatch({type: SET_SELECTED_ASSET, asset: assetInfo});
            if (trackingV2) {
              dispatch({type: GET_ASSOCIATED_ASSETS_ERROR, error: null});
            } else {
              dispatch({type: GET_ASSOCIATED_ASSETS_RESPONSE, assetId, associatedAssets: associatedAssetsData});
            }
            dispatch({type: SET_ASSET_DETAIL_VIEW, value: true});
          });
        }
      }
    } catch (err) {
      dispatch({type: GET_ASSOCIATED_ASSETS_ERROR, err});
      error(err);
    }
  };
};

/**
 * Retrieves the CVS (iOn Geodatabase) data for an associated asset.
 * If none are found, sets the selected asset to just an id as to be able to get Connect data.
 */
export const setAssociatedAssetDetail = (assetId: number) => {
  return async(dispatch, getState) => {
    dispatch({type: SET_ASSET_DETAIL_VIEW, value: true});
    try {
      dispatch({type: GET_ASSET_DETAILS_REQUEST});
      const groupIds = getCheckedAccountIds(getState);
      const cvsAssets = await getCVSAssets({
        groups : groupIds,
        assetId
      });
      const asset = cvsAssets?.length
        ? Devices.getAssetsFromCollection(cvsAssets)[0]
        : Devices.getAssetsFromCollection([{id: assetId}])[0];
      dispatch({type: GET_ASSET_DETAILS_RESPONSE, asset});
    } catch (err) {
      dispatch({type: GET_ASSET_DETAILS_ERROR, err});
      error(err);
    }
  };
};

interface AssetDetailViewProp {
  value: boolean,
  forcePanelToOpen?: boolean,
  fromTools?: boolean,
  isMapSelection?: boolean,
  selectedItem?: Devices | CVSAssetType;
  omitExitFindNearby?: boolean
};

/**
 * @setShowSideSheetDetail
 * @setAssetDetailView
 */
export const setAssetDetailView = ({
  value,
  forcePanelToOpen = false,
  fromTools = false,
  selectedItem = null,
  isMapSelection = null,
  omitExitFindNearby = false
}: AssetDetailViewProp) => {
  return async(dispatch, getState) => {
    const {
      query: {
        pathname,
        queryParams
      },
      tracking: {
        findNearby: {
          isActive
        },
        assets: {
          selectedAsset: selectedDevice
        },
        fleetMode: {
          isLargeFleetMode,
          largeFleetAssetList: {
            assets: largeFleetAssets
          }
        }
      }
    } = getState();
    isActive && !omitExitFindNearby && dispatch({type: EXIT_FIND_NEARBY});
    const selectedAsset = getItemValue(selectedItem ?? selectedDevice);
    dispatch({type: SET_ASSET_DETAIL_VIEW, value});
    if (!value && queryParams?.assetDetail) {
      dispatch({type: SET_QUERY_PARAMS, pathname, queryParams: null});
    }
    if (!selectedAsset?._self || fromTools) {
      try {
        dispatch({type: GET_ASSET_DETAILS_REQUEST});
        const assetAsDevice = await getAssetUpdated(largeFleetAssets, selectedAsset, isLargeFleetMode);
        dispatch({type: GET_ASSET_DETAILS_RESPONSE, asset: assetAsDevice, isMapSelection});
        dispatch(setSideSheetStatus({
          desktopSideSheetOpen : true
        }));
      } catch (err) {
        dispatch({type: GET_ASSET_DETAILS_ERROR, err});
        dispatch({type: SET_ASSET_DETAIL_VIEW, value: false});
        error(err);
      }
    }
    if (forcePanelToOpen) {
      dispatch(setSideSheetStatus({
        desktopSideSheetOpen : true
      }));
    }
  };
};

/**
 * @function setTrackingBaseMap
 */
export const setTrackingBaseMap = (basemap: BaseMapEnum) => {
  try {
    setDefaultBasemap(basemap);
    return ({
      type : SET_TRACKING_BASE_MAP, basemap
    });
  } catch (err) {
    error(err);
  };
};

/**
 * @function setTrackingMapLayers
 */
export const setTrackingMapLayers = (layers: string[]) => ({
  type : SET_TRACKING_MAP_LAYERS, layers
});

/**
 * @function setTrackingMapPosition
 */
export const setTrackingMapPosition = ({zoom, extent, center}) => ({
  type : SET_TRACKING_MAP_POSITION, zoom, extent, center
});

export const setTrackingMapCenter = (view: __esri.View) => ({
  type : SET_TRACKING_MAP_VIEW, view
});

/**
 * @function setTrackingMapZoom
 */
export const setTrackingMapZoom = zoom => ({
  type : SET_TRACKING_MAP_ZOOM, zoom
});

/**
 * @function setTrackingMapExtent
 */
export const setTrackingMapExtent = extent => ({
  type : SET_TRACKING_MAP_EXTENT, extent
});

/**
 * @function setInitialTrackingMapExtent
 */
export const setInitialTrackingMapExtent = extent => ({
  type : SET_INITIAL_TRACKING_MAP_EXTENT, extent
});

/**
 * @function setMapviewInitialExtent
 */
export const setMapviewInitialExtent = extent => ({
  type : SET_TRACKING_MAPVIEW_INITIAL_EXTENT, extent
});

/**
 * @function setMapviewExtent
 */
export const setMapviewExtent = extent => ({
  type : SET_TRACKING_MAPVIEW_EXTENT, extent
});

/**
 * @function setTrafficLayerOpacity
 */
export const setTrafficLayerOpacity = (opacity: number) => ({
  type : SET_TRAFFIC_LAYER_OPACITY, opacity
});

/**
 * @function setTrafficLayerOpacity
 */
export const setTrafficLayerSubLayers = (trafficSubLayers: string[]) => ({
  type : SET_TRAFFIC_LAYER_SUBLAYERS, trafficSubLayers
});

/**
 * @function setWeatherLayerOpacity
 */
export const setWeatherLayerOpacity = (opacity: number) => ({
  type : SET_WEATHER_LAYER_OPACITY, opacity
});

/**
 * @function setGeofenceLayerOpacity
 */
export const setGeofenceLayerOpacity = (opacity: number) => ({
  type : SET_GEOFENCE_LAYER_OPACITY, opacity
});

/**
 * @function setToolDetailView
 */
export const setToolDetailView = (value: boolean) => {
  return {type: SET_TOOL_DETAIL_VIEW, value};
};

/**
 * @function setTool
 */
export const setTool = (tool: ToolAssetType) => {
  return {type: SET_SELECTED_TOOL, tool};
};

/**
 * @function toggleMapControls
 */
export const toggleMapControls = (status: boolean) => {
  return {type: TOGGLE_MAP_CONTROLS, status};
};

/**
 * @param toggleAssetClustering
 */
export const toggleAssetClustering = (status: boolean, fromMap: boolean = false) => {
  return {type: TOGGLE_ASSET_CLUSTERING, status, fromMap};
};

export const toggleAssetLabelling = (status: boolean) => ({
  type : TOGGLE_ASSET_LABELLING, status
});

export const onChangeAssetContent = (labelContent: LabelContentType) => ({
  type : SET_ASSET_CONTENT, labelContent
});

export const toggleDeviceLabelling = (status: boolean) => ({
  type : TOGGLE_DEVICE_LABELLING, status
});

export const onChangeDeviceLabelContent = (labelContent: LabelContentType) => ({
  type : SET_DEVICE_LABEL_CONTENT, labelContent
});

/**
 * @function setProcessedTrackingFilters
 */
export const setProcessedTrackingFilters = (value: object[]) => (
  {type: SET_PROCESSED_FILTERS, value});

/**
 * @function setSelectedFilterName
 */
export const setSelectedFilterName = (value: string) => (
  {type: SET_FILTER_NAME, value});

/**
 * @function setTrackingFilters
 */
export const setTrackingFilters = (value: TrackingFilters) => (
  {type: SET_TRACKING_FILTERS, value});

/**
 * @function setGroupStatus
 */
export const setGroupStatus = (value: AccountHashMapKeyType) => ({
  type : SET_GROUP_STATUS, value
});

/**
 * @function setSideSheetView
 */
export const setSideSheetView = (id: SideSheetView) => ({
  type     : SET_SIDESHEET_VIEW,
  viewType : id
});

/**
 * @function setMapOnlyView
 */
export const setMapOnlyView = (isMapOnlyView: boolean) => ({
  type : SET_MAP_ONLY_VIEW,
  isMapOnlyView
});

// invoked when the bottom grid height needs to be set to store...
export const setBottomGridHeight = (height: number) => (
  {type: SET_BOTTOM_GRID_HEIGHT, height}
);

export const changeAssetsSortingOrder = (order) => ({
  type : SET_ASSETS_SORT_DESCENDING,
  order
});

/**
 * Uses user's accounts within the user's hierarchy within the store
 * via getStore().initialization.accounts. The accounts
 * in the store are HATEOAS Links (href, title, etc.). The utility
 * that creates the checkbox tree uses this data format.
 */
export const getGroupFilters = () => {
  return async(dispatch, getState) => {
    dispatch({type: GET_GROUPS_FILTER_REQUEST});
    dispatch({type: TRACKING_ASSETS_REQUEST});
    try {
      const fullHierarchy = getSelectedGroupHierarchy(getState);
      dispatch({
        type  : GET_GROUPS_FILTER_RESPONSE,
        items : fullHierarchy
      });
      dispatch({type: TRACKING_ASSETS_RESPONSE});
    } catch (error) {
      dispatch({type: GET_GROUPS_FILTER_ERROR, error});
    }
  };
};

/**
 * Retrieves the asset type and categories filters (sub-account hierarchy)
 * for the provided accountId.
 * If the accountId is not provided, the user's default account id will be used.
 */
export const getAssetTypesAndCategoryFilters = (
  accountId?: number,
  assets?: CVSAssetType[]
) => {
  return async(dispatch, getState) => {
    dispatch({type: GET_ASSET_TYPE_AND_CATEGORY_FILTER_REQUEST});
    dispatch({type: TRACKING_ASSETS_REQUEST});
    try {
      const groups = getAllAccountIds(getState, accountId);
      const items  = await getTypesAndCategories(groups, entityIds(assets));
      dispatch({
        type : GET_ASSET_TYPE_AND_CATEGORY_FILTER_RESPONSE,
        ...items
      });
      dispatch({type: TRACKING_ASSETS_RESPONSE});
    } catch (error) {
      dispatch({type: GET_ASSET_TYPE_AND_CATEGORY_FILTER_ERROR, error});
    }
  };
};

/**
 * Retrieves the geofence filters (sub-account hierarchy) for the provided accountId.
 * If the accountId is not provided, the user's default account id will be used.
 */
export const getGeoFenceFilters = (
  accountId?: number
) => {
  return async(dispatch, getState) => {
    dispatch({type: GET_GEOFENCE_FILTER_REQUEST});
    dispatch({type: TRACKING_ASSETS_REQUEST});
    try {
      let getAncestorAccountValue;
      let ancestorAccountIds;
      const ancestorAccountId   = getState().globalGroup.ancestorAccountId;
      if (ancestorAccountId !== null) {
        getAncestorAccountValue = Object.values(ancestorAccountId)[0];
        if (!isUndefined(getAncestorAccountValue)) {
          ancestorAccountIds  = Object.keys(getAncestorAccountValue).toString();
        }
      }
      const {globalGroup, accountHierarchy} = getState().globalGroup;
      const [match]                         = findCurrentAccountMatch(accountHierarchy, globalGroup);
      const descendantAccountId             = entityId(match?.href);
      const query                           = loadLandmarkGeoFences(ancestorAccountIds, descendantAccountId);
      const items                           = await searchGeoFences({query});
      dispatch({
        type : GET_GEOFENCE_FILTER_RESPONSE,
        items
      });
      dispatch({type: TRACKING_ASSETS_RESPONSE});
    } catch (error) {
      dispatch({type: GET_GEOFENCE_FILTER_ERROR, error});
    }
  };
};

// Defines a utility to handle filters change
const onFilterStateChange = (type: string, dispatch) => {
  batch(() => {
    type && dispatch({type});
    dispatch({type: TRACKING_ASSETS_REQUEST});
    dispatch(setHistoryReportStatus());
  });
};

/**
 * @function setGroupFilters
 */
export const setGroupFilters = (
  value,
  trackingMode?: TrackingMode,
  callback?: Function
) => {
  return async(dispatch, getState) => {
    try {
      const {
        selectedAssets,
        selectedGroups,
        selectedDevices
      } = value || {};
      const isLagacyTracking = !isTracking2(getState);
      if (isLagacyTracking && isLiveTracking(trackingMode)) {
        dispatch(setTrackingAssetCountLoad(true));
        onFilterStateChange(SET_GROUPS_FILTER_REQUEST, dispatch);
        onFilterStateChange(SET_ASSETS_FILTER_REQUEST, dispatch);
        onFilterStateChange(SET_DEVICES_FILTER_REQUEST, dispatch);
        let accountIds;
        let deviceIds;
        let assets = [];
        const groupIds = [];

        // if asset or group is selected by user
        if (selectedAssets?.length || selectedGroups?.length || selectedDevices?.length) {
          accountIds = selectedGroups.map(group => group?.id);
          assets = getFilteredAssets(selectedAssets, accountIds);
          deviceIds = selectedDevices?.map(device => device?.id);
        } else {
          // default case
          accountIds = value?.length > 0
            ? value?.map(option => option?.id ?? option) : getAllAccountIds(getState);
        }
        const {
          tracking:
        {
          fleetMode: {
            isLargeFleetMode: largeFleetModeState
          },
          filters: {
            assetTypes,
            assetCategories,
            geoFences,
            routes,
            assetStatus,
            boundary
          },
          grid : {
            filterModel
          }
        }
        }        = getState();
        const filter  = largeFleetModeState ? filterModelConverterV2(filterModel, attributeMappings) : null;
        const {statusFilter, addressFilterObj} = getAdditionalFilterParams(filter);
        const assetStatusParams                = [...getFilterRequestParams(assetStatus.selected)];
        statusFilter && assetStatusParams.push(statusFilter);
        const params = {
          groups          : accountIds,
          assets,
          assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
          assetCategories : [...getFilterRequestParams(assetCategories.selected)],
          geoFences       : [...getFilterRequestParams(geoFences.selected)],
          routes          : [...getFilterRequestParams(routes.selected)],
          assetStatus     : assetStatusParams,
          geometry        : boundary,
          filter,
          ...addressFilterObj
        };
        const response = await getAssetCount(params);
        dispatch(validateLargeFleetMode({largeFleetModeState, response, params, selectedDevices: deviceIds}));
        dispatch({type: TRACKING_ASSETS_RESPONSE});
      }
      dispatch({type: SET_GROUPS_FILTER_RESPONSE, value: selectedGroups || []});
      dispatch({type: SET_ASSETS_FILTER_RESPONSE, value: selectedAssets || []});
      dispatch({type: SET_DEVICES_FILTER_RESPONSE, value: selectedDevices || []});
      isLagacyTracking && Features.hasSCIAccess() && dispatch(loadSCDevices());
      callback?.();
    } catch (error) {
      dispatch({type: SET_GROUPS_FILTER_ERROR, error});
      dispatch({type: SET_ASSETS_FILTER_ERROR, error});
      dispatch({type: SET_DEVICES_FILTER_ERROR, error});
    }
  };
};

/**s
 * Defines a utility to set the boundary filter
 */
export const setBoundaryFilter = boundary => ({type: SET_BOUNDARY_FILTER, boundary});

/**
 * @function applyBoundaryFilter
 */
export const applyBoundaryFilter = (boundaryData, onlyFilter: boolean = false) => {
  return async(dispatch, getState) => {
    try {
      const isLagacyTracking = !isTracking2(getState);
      if (isLagacyTracking) {
        const groupIds = getCheckedAccountIds(getState);
        const {
          tracking: {
            trackingMode,
            findNearby: {
              isActive
            },
            fleetMode: {
              isLargeFleetMode: largeFleetModeState
            },
            filters: {
              assetCategories,
              geoFences,
              routes,
              assetStatus,
              assetTypes,
              assets: {
                selected: assetsSelected
              },
              devices
            },
            grid : {
              filterModel
            }
          }
        } = getState();
        const filter = largeFleetModeState ? filterModelConverterV2(filterModel, attributeMappings) : null;
        const {statusFilter, addressFilterObj} = getAdditionalFilterParams(filter);
        const assetStatusParams                = [...getFilterRequestParams(assetStatus.selected)];
        statusFilter && assetStatusParams.push(statusFilter); ;
        const {
          selected: selectedDevices
        } = devices || {};
        dispatch(setBoundaryFilter(boundaryData));
        // in case of clear boundryFilter, geometry array is length === 0
        // in case of apply boundryFilter, geometry array is length !== 0
        const isClear = boundaryData?.length === 0;
        dispatch(setRouteMatchingEnabled(isClear));
        if (trackingMode === TrackingMode.Live && !isActive && !onlyFilter) {
          const params = {
            groups          : groupIds,
            assets          : getFilteredAssets(assetsSelected, groupIds),
            assetCategories : [...getFilterRequestParams(assetCategories.selected)],
            geoFences       : [...getFilterRequestParams(geoFences.selected)],
            routes          : [...getFilterRequestParams(routes.selected)],
            assetStatus     : assetStatusParams,
            assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
            geometry        : boundaryData,
            filter,
            ...addressFilterObj
          };
          dispatch({type: TRACKING_ASSETS_REQUEST});
          const response = await getAssetCount(params);
          dispatch(
            validateLargeFleetMode({largeFleetModeState, response, params, selectedDevices})
          );
          Features.hasSCIAccess() && dispatch(loadSCDevices());
          dispatch({type: TRACKING_ASSETS_RESPONSE});
        }
      } else {
        dispatch(setBoundaryFilter(boundaryData));
        dispatch(setRouteMatchingEnabled(boundaryData?.length === 0));
      }
    } catch (err) {
      error(err);
    }
  };
};

/**
 * @function setAssetTypeFilters
 */
export const setAssetTypeFilters = (value, trackingMode = TrackingMode.Live) => {
  return async(dispatch, getState) => {
    try {
      const isLagacyTracking = !isTracking2(getState);
      if (isLagacyTracking) {
        const {
          nav: {
            currentPage
          },
          tracking:
        {
          fleetMode: {
            isLargeFleetMode: largeFleetModeState
          },
          filters: {
            assetCategories,
            geoFences,
            routes,
            assetStatus,
            assets : {
              selected : assetsSelected
            },
            devices,
            boundary
          },
          grid : {
            filterModel
          }
        }
        } = getState();
        if (!isCurrentPage(currentPage, Route.TRACKING2) && isLiveTracking(trackingMode)) {
          dispatch(setTrackingAssetCountLoad(true));
          onFilterStateChange(SET_ASSET_TYPE_FILTER_REQUEST, dispatch);
          const groupIds = getCheckedAccountIds(getState);
          const filter = largeFleetModeState ? filterModelConverterV2(filterModel, attributeMappings) : null;
          const {statusFilter, addressFilterObj} = getAdditionalFilterParams(filter);
          const assetStatusParams                = [...getFilterRequestParams(assetStatus.selected)];
          statusFilter && assetStatusParams.push(statusFilter);
          const {
            selected : selectedDevices
          } = devices || {};
  
          const params = {
            groups          : groupIds,
            assets          : getFilteredAssets(assetsSelected, groupIds),
            assetTypes      : [...getFilterRequestParams(value)],
            assetCategories : [...getFilterRequestParams(assetCategories.selected)],
            geoFences       : [...getFilterRequestParams(geoFences.selected)],
            routes          : [...getFilterRequestParams(routes.selected)],
            assetStatus     : assetStatusParams,
            geometry        : boundary,
            filter,
            ...addressFilterObj
          };
          const response = await getAssetCount(params);
          dispatch(validateLargeFleetMode({largeFleetModeState, response, params, selectedDevices}));
          Features.hasSCIAccess() && dispatch(loadSCDevices());
          dispatch({type: TRACKING_ASSETS_RESPONSE});
        }
      }
      dispatch({type: SET_ASSET_TYPE_FILTER_RESPONSE, value});
    } catch (error) {
      dispatch({type: SET_ASSET_TYPE_FILTER_ERROR, error});
    }
  };
};

/**
 * @function setCategoryFilters
 */
export const setCategoryFilters = (value, trackingMode = TrackingMode.Live) => {
  return async(dispatch, getState) => {
    try {
      const isLagacyTracking = !isTracking2(getState);
      if (isLagacyTracking && isLiveTracking(trackingMode)) {
        dispatch(setTrackingAssetCountLoad(true));
        onFilterStateChange(SET_CATEGORY_FILTER_REQUEST, dispatch);
        const groupIds = getCheckedAccountIds(getState);
        const {
          tracking:
        {
          fleetMode: {
            isLargeFleetMode: largeFleetModeState
          },
          filters: {
            assetTypes,
            geoFences,
            routes,
            assetStatus,
            assets : {
              selected : assetsSelected
            },
            devices,
            boundary
          },
          grid : {
            filterModel
          }
        }
        }          = getState();
        const filter  = largeFleetModeState ? filterModelConverterV2(filterModel, attributeMappings) : null;
        const {statusFilter, addressFilterObj} = getAdditionalFilterParams(filter);
        const assetStatusParams                = [...getFilterRequestParams(assetStatus.selected)];
        statusFilter && assetStatusParams.push(statusFilter);
        const {
          selected : selectedDevices
        } = devices || {};

        const params = {
          groups          : groupIds,
          assets          : getFilteredAssets(assetsSelected, groupIds),
          assetCategories : [...getFilterRequestParams(value)],
          assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
          geoFences       : [...getFilterRequestParams(geoFences.selected)],
          routes          : [...getFilterRequestParams(routes.selected)],
          assetStatus     : assetStatusParams,
          geometry        : boundary,
          filter,
          ...addressFilterObj
        };
        const response = await getAssetCount(params);
        dispatch(validateLargeFleetMode({largeFleetModeState, response, params, selectedDevices}));
        Features.hasSCIAccess() && dispatch(loadSCDevices());
        dispatch({type: TRACKING_ASSETS_RESPONSE});
      }
      dispatch({type: SET_CATEGORY_FILTER_RESPONSE, value});
    } catch (error) {
      dispatch({type: SET_CATEGORY_FILTER_ERROR, error});
    }
  };
};

/**
  * @function setGeofenceFilters
  */
export const setGeofenceFilters = (value) => {
  return async(dispatch, getState) => {
    try {
      const isLagacyTracking = !isTracking2(getState);
      if (isLagacyTracking) {
        dispatch(setTrackingAssetCountLoad(true));
        onFilterStateChange(SET_GEOFENCE_FILTER_REQUEST, dispatch);
        const groupIds = getCheckedAccountIds(getState);
        const {
          tracking:
          {
            fleetMode: {
              isLargeFleetMode: largeFleetModeState
            },
            filters: {
              assetTypes,
              assetCategories,
              routes,
              assetStatus,
              assets : {
                selected : assetsSelected
              },
              devices,
              boundary
            },
            grid : {
              filterModel
            }
          }
        }          = getState();
        const filter = largeFleetModeState ? filterModelConverterV2(filterModel, attributeMappings) : null;
        const {statusFilter, addressFilterObj} = getAdditionalFilterParams(filter);
        const assetStatusParams                = [...getFilterRequestParams(assetStatus.selected)];
        statusFilter && assetStatusParams.push(statusFilter);
        const {
          selected : selectedDevices
        } = devices || {};
  
        const params = {
          groups          : groupIds,
          assets          : getFilteredAssets(assetsSelected, groupIds),
          geoFences       : [...getFilterRequestParams(value)],
          assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
          assetCategories : [...getFilterRequestParams(assetCategories.selected)],
          routes          : [...getFilterRequestParams(routes.selected)],
          assetStatus     : assetStatusParams,
          geometry        : boundary,
          filter,
          ...addressFilterObj
        };
        const response = await getAssetCount(params);
        dispatch(validateLargeFleetMode({largeFleetModeState, response, params, selectedDevices}));
        Features.hasSCIAccess() && dispatch(loadSCDevices());
        dispatch({type: TRACKING_ASSETS_RESPONSE});
      }
      dispatch({type: SET_GEOFENCE_FILTER_RESPONSE, value});
    } catch (error) {
      dispatch({type: SET_GEOFENCE_FILTER_ERROR, error});
    }
  };
};

// invokes when the save filter is applied or removed....
export const applySaveFilter = (filterName, filterState, isResetTracking = false) => {
  return async(dispatch, getState) => {
    try {
      const isLagacyTracking = !isTracking2(getState);
      if (isLagacyTracking) {
        dispatch(setTrackingAssetCountLoad(true));
        dispatch({type: TRACKING_ASSETS_REQUEST});
        const assetExtent  = [];
        const hasSCIAccess = Features.hasSCIAccess();
        dispatch({type: SET_SAVE_FILTER_REQUEST, value: {filterName, filterState}});
        onFilterStateChange(null, dispatch);
        dispatch(resetSelectedAsset());
        const groupIds = getCheckedAccountIds(getState);
        const {
          nav: {
            currentPage
          },
          tracking:
          {
            fleetMode: {
              isLargeFleetMode: largeFleetModeState
            }
          },
          globalGroup: {
            globalGroupHierarchy
          }
        } = getState();
  
        const {selected}    = filterState.assets || [];
        const assetsAllowed = getItemAllowed(selected, globalGroupHierarchy);
        const {
          geoFences,
          assetTypes,
          assetCategories,
          routes,
          assetStatus,
          devices,
          date,
          boundary = []
        } = filterState || {};
        dispatch(setBoundaryFilter(boundary));
        if (!isResetTracking && (date && date?.period !== DateSelectorValues.Live)) {
          dispatch({type: SET_ASSETS_FILTER_RESPONSE, value: assetsAllowed});
          dispatch(setTrackingMode(TrackingMode.History));
        } else {
          dispatch(setTrackingMode(TrackingMode.Live));
          const {selected: selectedDevices} = devices || {};
          const params = {
            groups          : getCheckedAccountIds(getState),
            assets          : getFilteredAssets(assetsAllowed, groupIds),
            geoFences       : [...getFilterRequestParams(geoFences.selected)],
            assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
            assetCategories : [...getFilterRequestParams(assetCategories.selected)],
            routes          : [...getFilterRequestParams(routes.selected)],
            assetStatus     : [...getFilterRequestParams(assetStatus.selected)],
            geometry        : boundary
          };
          const fleetResponse  = await getAssetCount(params);
          const totalCount     = (fleetResponse?.count ?? 0) + (selectedDevices?.length ?? 0);
          const largeFleetMode = getIsLargeFleetMode(totalCount);
          assetExtent.push(fleetResponse);
          if (!largeFleetMode && hasSCIAccess) {
            const cargoParams = getCargoParams({
              selectedAssets : selected,
              getState,
              selectedDevices,
              globalGroupHierarchy
            });
            const cargoReponse = await getCargoCount(cargoParams);
            assetExtent.push(cargoReponse);
          }
          if (isResetTracking) {
            dispatch(setMapInitialExtent(assetExtent));
          }
          dispatch(setAssetsCount(largeFleetMode, totalCount));
          dispatch({type: SET_LARGE_FLEET_STATE, largeFleetMode, response: fleetResponse, refreshMap: true});
          if (!largeFleetMode) {
            const assets = await getCVSAssets(params);
            const assetsAsDevices = Devices.getAssetsFromCollection(assets);
            if (isResetTracking) {
              dispatch(setTrackingMapExtent(null));
            }
            dispatch({type: INITIALIZE_TRACKING, assets: assetsAsDevices});
          }
          dispatch(updateLargeFleetAssetList(largeFleetModeState, largeFleetMode, params));
          dispatch({type: TRACKING_ASSETS_RESPONSE});
          hasSCIAccess && dispatch(loadSCDevices());
        }
      } else {
        const {date} = filterState || {};
        const isLiveMode = date?.period === DateSelectorValues.Live;
        dispatch(setTrackingMode(isLiveMode ? TrackingMode.Live : TrackingMode.History));
        dispatch({type: SET_SAVE_FILTER_REQUEST, value: {filterName, filterState}});
      }
    } catch (error) {
      dispatch({type: SET_SAVE_FILTER_ERROR, error});
    }
  };
};

/**
 * @function setRouteFilters
 */
export const setRouteFilters = (value) => {
  return async(dispatch, getState) => {
    try {
      const isLagacyTracking = !isTracking2(getState);
      if (isLagacyTracking) {
        dispatch(setTrackingAssetCountLoad(true));
        onFilterStateChange(SET_ROUTE_FILTER_REQUEST, dispatch);
        const groupIds = getCheckedAccountIds(getState);
        const {
          tracking:
          {
            fleetMode: {
              isLargeFleetMode: largeFleetModeState
            },
            filters: {
              assetTypes,
              assetCategories,
              geoFences,
              assetStatus,
              assets : {
                selected : assetsSelected
              },
              devices,
              boundary
            },
            grid : {
              filterModel
            }
          }
        }          = getState();
        const filter = largeFleetModeState ? filterModelConverterV2(filterModel, attributeMappings) : null;
        const {statusFilter, addressFilterObj} = getAdditionalFilterParams(filter);
        const assetStatusParams                = [...getFilterRequestParams(assetStatus.selected)];
        statusFilter && assetStatusParams.push(statusFilter);
        const {selected: selectedDevices} = devices || {};
  
        const params = {
          groups          : groupIds,
          assets          : getFilteredAssets(assetsSelected, groupIds),
          routes          : [...getFilterRequestParams(value)],
          assetCategories : [...getFilterRequestParams(assetCategories.selected)],
          assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
          geoFences       : [...getFilterRequestParams(geoFences.selected)],
          assetStatus     : assetStatusParams,
          geometry        : boundary,
          filter,
          ...addressFilterObj
        };
        const response = await getAssetCount(params);
        dispatch(validateLargeFleetMode({largeFleetModeState, response, params, selectedDevices}));
        Features.hasSCIAccess() && dispatch(loadSCDevices());
        dispatch({type: TRACKING_ASSETS_RESPONSE});
      }
      dispatch({type: SET_ROUTE_FILTER_RESPONSE, value});
    } catch (error) {
      dispatch({type: SET_ROUTE_FILTER_ERROR, error});
    }
  };
};

/**
 * Sets/resets the state of asset status filters when applied/cleared ...
 */
export const setAssetStatusFilters = (value) => {
  return async(dispatch, getState) => {
    try {
      const isLagacyTracking = !isTracking2(getState);
      if (isLagacyTracking) {
        dispatch(setTrackingAssetCountLoad(true));
        onFilterStateChange(SET_ASSET_STATUS_FILTER_REQUEST, dispatch);
        const groupIds = getCheckedAccountIds(getState);
        const {
          nav: {
            currentPage
          },
          tracking: {
            fleetMode: {
              isLargeFleetMode: largeFleetModeState
            },
            filters: {
              assetTypes,
              assetCategories,
              geoFences,
              routes,
              assets : {
                selected : assetsSelected
              },
              devices,
              boundary
            },
            grid : {
              filterModel
            }
          }
        }          = getState();
        const filter = largeFleetModeState ? filterModelConverterV2(filterModel, attributeMappings) : null;
        const {statusFilter, addressFilterObj} = getAdditionalFilterParams(filter);
        const assetStatusParams                = [...getFilterRequestParams(value)];
        statusFilter && assetStatusParams.push(statusFilter);
        const {
          selected : selectedDevices
        } = devices || {};
  
        const params = {
          groups          : groupIds,
          assets          : getFilteredAssets(assetsSelected, groupIds),
          assetCategories : [...getFilterRequestParams(assetCategories.selected)],
          assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
          geoFences       : [...getFilterRequestParams(geoFences.selected)],
          routes          : [...getFilterRequestParams(routes.selected)],
          assetStatus     : assetStatusParams,
          geometry        : boundary,
          filter,
          ...addressFilterObj
        };
        const response = await getAssetCount(params);
        const totalCount = (response?.count ?? 0) + (selectedDevices?.length ?? 0);
        const largeFleetMode = getIsLargeFleetMode(totalCount);
        batch(async() => {
          dispatch(setAssetsCount(largeFleetMode, totalCount));
          dispatch({type: SET_LARGE_FLEET_STATE, largeFleetMode, response, refreshMap: true});
          if (!largeFleetMode) {
            const assets = await getCVSAssets(params);
            const assetsAsDevices = Devices.getAssetsFromCollection(assets);
            dispatch({type: INITIALIZE_TRACKING, assets: assetsAsDevices});
          }
          dispatch({type: SET_ASSET_STATUS_FILTER_RESPONSE, value});
          dispatch(updateLargeFleetAssetList(largeFleetModeState, largeFleetMode, params));
          dispatch({type: TRACKING_ASSETS_RESPONSE});
          Features.hasSCIAccess() && dispatch(loadSCDevices());
        });
      } else {
        dispatch({type: SET_ASSET_STATUS_FILTER_RESPONSE, value});
      }
    } catch (error) {
      dispatch({type: SET_ASSET_STATUS_FILTER_ERROR, error});
    }
  };
};

/**
  * @function setGeoFenceFilterOpen
  */
export const setGeoFenceFilterOpen = (value: boolean) => ({
  type : SET_GEOFENCE_FILTER_OPEN, value
});

/**
 * @function setSearchHistory
 */
export const setSearchHistory = searchHistory => ({
  type : SET_SEARCH_HISTORY, searchHistory
});

/**
 * @function savingGeofence
 */
export const savingGeofence = value => ({
  type : SAVING_GEOFENCE, value
});

/**
 * @function setStatusToResumeMap
 */
export const setStatusToResumeMap = (status) => {
  return {
    type : SET_STATUS_TO_RESUME_MAP, status
  };
};

/**
 * @function initializeAllFilters
 */
export const initializeAllFilters = (
  initialize = true,
  reset?: boolean,
  accounts?: AccountHashMapKeyType
) => {
  return async(dispatch) => {
    dispatch({type: SET_ALL_FILTERS_LOADING_STATE, status: false});
    if (initialize) {
      await dispatch(getGroupFilters());
      await dispatch(getAssetTypesAndCategoryFilters());
      await dispatch(getGeoFenceFilters());
      dispatch({type: SET_ALL_FILTERS_LOADING_STATE, status: true});
    } else {
      await dispatch(getGroupFilters());
      dispatch(setGroupStatus(accounts));
      await dispatch(getAssetTypesAndCategoryFilters());
      await dispatch(getGeoFenceFilters());
      dispatch({type: SET_ALL_FILTERS_LOADING_STATE, status: true});
      if (reset) {
        dispatch(initializeTracking());
      }
    }
  };
};

/**
 * @deprecated
 * @function applyAllFilters
 */
export const applyAllFilters = ({
  savedFilters,
  currentFilters
}) => {
  return async(dispatch, getState) => {
    dispatch({type: SET_ALL_FILTERS_LOADING_STATE, status: false});
    await dispatch(getGroupFilters());
    await dispatch(getAssetTypesAndCategoryFilters());
    await dispatch(getGeoFenceFilters());
    for (const key in savedFilters) {
      const value = savedFilters[key]?.selections;
      if (key === AllFilterTypes.Groups) {
        await dispatch(setGroupFilters(value));
      }
      if (key === AllFilterTypes.Assets) {
        await dispatch(setAssetTypeFilters(value));
      }
      if (key === AllFilterTypes.Categories) {
        await dispatch(setCategoryFilters(value));
      }
      if (key === AllFilterTypes.Geofences) {
        const updatedValues = currentFilters;
        // Latest list of geofence filter items available.
        const geoItems = getState().tracking?.filters?.geoFences?.items;
        const selections = await getGeoFenceFilterSelections(geoItems, value);
        /* if the selected filters from Applied saved filter is not a
           subset of latest list of geofence items,
           then updating the userpreference with latest values */
        const names = geoItems.map(({name}) => name);
        const isSuperset = value.every(({name}) => {
          return names.indexOf(name) >= 0;
        });
        if (!isSuperset) {
          updatedValues.initialFilters[AllFilterTypes.Geofences].selected = selections;
          updatedValues.initialFilters[AllFilterTypes.Geofences].items = geoItems;
          updatedValues.filters.map((element) => {
            if (element.key === AllFilterTypes.Geofences) {
              element.selected = selections;
              element.items = geoItems;
            }
          });
          updateUserPreference(updatedValues);
        }
        // Applying only the filter items that are present in latest geofence items list.
        await dispatch(setGeofenceFilters(selections));
      }
    }
    dispatch({type: SET_ALL_FILTERS_LOADING_STATE, status: true});
  };
};

/**
 * @function saveGeofence
 */
export const saveGeofence = (
  {
    isUpdate = false,
    landmarkName = null,
    geofence, isDelete = false,
    shouldShowSuccessPrompt = true,
    isAdminGeofence = false
  }:
  {
    isUpdate?: boolean;
    landmarkName?: string;
    geofence: { rings: []; attrs: GeoFenceAttributes; };
    isDelete?: boolean;
    shouldShowSuccessPrompt?: boolean;
    isAdminGeofence?: boolean;
  }) => {
  return async(dispatch, getState) => {
    dispatch(savingGeofence(true));
    if (isAdminGeofence) {
      dispatch({type: ADD_ADMIN_GEOFENCES_REQUEST});
    }

    const {
      globalGroup : {
        globalGroup: {
          title: accountTitle = '',
          href: accountHef = ''
        } = {}
      } = {}
    } = getState();
    const accountId = entityId(accountHef);
    const {rings, attrs} = geofence;
    const status: GeoFenceStatus = {
      isCreate : (!isDelete && !isUpdate),
      isDelete,
      isUpdate,
      success  : false
    };
    const data = {
      ...attrs,
      landmarkName : attrs?.landmarkName?.trim()
    };
    try {
      let response;
      if (!isDelete) {
        response = await createGeoFence(data, rings, isUpdate);
      } else {
        response = await deleteGeoFence(data, rings);
      }
      const [{objectId, success}] = response;
      if (!success) error((!isDelete ? 'could not save geofence' : 'could not delete geofence'));
      const savedGeofence = {
        ...data,
        rings,
        objectId
      };
      status.success = success;
      const togglePromptPromise = shouldShowSuccessPrompt
        ? dispatch(toggleUserPrompt({message: getMessage(status)}))
        : null;
      batch(() => {
        dispatch({type: SAVE_GEOFENCE, savedGeofence});
        dispatch({
          type        : APPLY_GEOFENCE_TO_FILTER,
          name        : data.landmarkName,
          id          : objectId,
          updatedName : landmarkName?.trim(),
          isDelete,
          isUpdate
        });
        dispatch({type: SAVE_GEOFENCE_SUCCESS, status});
        isAdminGeofence && dispatch({type: ADD_ADMIN_GEOFENCES_RESPONSE, geofence: status});
        dispatch(setGeoFencePanelOpen(false));
        dispatch(savingGeofence(false));
        togglePromptPromise;
      });
    } catch (err) {
      error(err);
      dispatch({type: SAVE_GEOFENCE_ERROR, status});
      if (isAdminGeofence) {
        dispatch({type: ADD_ADMIN_GEOFENCES_ERROR, error: err});
      }
      dispatch(savingGeofence(false));
      dispatch(toggleUserPrompt({message: err?.message}));
    }
  };
};

const getMessage = (status?: GeoFenceStatus) => {
  if (status) {
    const {success, isCreate, isDelete, isUpdate} = status;
    const operation = !isCreate ? (isDelete ? GeoFenceActions.Delete
      : isUpdate ? GeoFenceActions.Update : null) : GeoFenceActions.Create;
    if (operation) {
      return `tracking:geofenceNotification.${operation}Geofence${success ? 'Success' : 'Error'}`;
    }
  }
  return '';
};

/**
 * SMARTER TRAILER
 */

/**
 * Action creator that handles the logic for checking if
 * device has been checked for smarter trailer, fetching the
 * device and checking for smarter trailer sensors, then retrieving
 * the most recent readings.
 *
 * Actions keep track of which devices have been checked and which devices
 * include smarter trailer sensors
 */
export const loadSmartTrailer = (asset: Devices) => {
  return async(dispatch: Function, getState: Function): Promise<void> => {
    const {
      tracking : {
        assets : {
          smarterTrailer : {
            devicesHaveTrailerSensors,
            devicesChecked,
            selectedAssetTrailer,
            loading
          }
        }
      }
    } = getState();
    if (!loading) {
      dispatch({type: LOAD_SMARTER_TRAILER_REQUEST});
      try {
        let sensorApps         = hasSensorApps();
        let sensorData         = null;
        let concatSensorApps   = null;
        const {deviceEsn: esn} = asset?.getDevice();
        const deviceHasSmarterTrailer = devicesHaveTrailerSensors.find(
          ({esn: deviceHasSensorEsn}) => deviceHasSensorEsn === esn
        );
        // First check if the user has any of the sensor apps
        if (sensorApps.length) {
          // check if we've already checked the asset's device for smarter trailer sensors
          if (!deviceHasSmarterTrailer) {
            // device is not in our list, check if we've already checked it
            // only fetch the devices if we haven't check it yet
            if (!devicesChecked.includes(esn)) {
              // never checked this device, get the device details
              const [{features}]: Device[] = await getDevice(parseInt(esn), false, true);
              sensorApps = getAvailableSensorApps(sensorApps, features);
              if (sensorApps.length) {
                if (sensorApps.includes(SensorTypes.DoorState)) {
                  sensorData = await getDoorSensorHistory(esn);
                }
                sensorApps = trimDoorStateFromApps(sensorApps);
                if (sensorApps.length) {
                  concatSensorApps = sensorApps.join(', ');
                  // device has smarter trailer sensors, add it to list and get latest data
                  const latestData = await getLatestSensorData(esn, concatSensorApps);
                  sensorData = sensorData ? sensorData.concat(latestData) : latestData;
                }
                dispatch({type: ADD_DEVICE_WITH_SMARTER_TRAILER, esn, apps: sensorApps});
                // add this device to the list of checked devices
                dispatch({type: SMARTER_TRAILER_CHECKED, esn});
              }
            }
            // already checked this device, there is no smarter trailer, NO-OP
          } else {
            concatSensorApps = deviceHasSmarterTrailer?.apps?.join(', ');
            // we already know that this device has smarter trailer, get the latest data
            sensorData = await getLatestSensorData(esn, concatSensorApps);
            if (concatSensorApps?.includes(SensorTypes.DoorState) ||
              sensorApps?.includes(SensorTypes.DoorState)) {
              sensorData = sensorData.concat(await getDoorSensorHistory(esn));
            }
          }
        }
        const augmentedData = augmentSensorData(
          selectedAssetTrailer,
          sensorData,
          isSameESN(esn, selectedAssetTrailer)
        );
        dispatch({type: LOAD_SMARTER_TRAILER_RESPONSE, trailer: augmentedData});
        dispatch({type: SMARTER_TRAILER_SENSOR_NEEDS_UPDATE, value: false});
      } catch (err) {
        dispatch({type: LOAD_SMARTER_TRAILER_ERROR, err});
      }
    }
  };
};

/**
 * Here we already know if a device has smarter trailer and we just need to
 * get he most updated info
 */
export const updateSensorData = () => {
  return async(dispatch: Function, getState: Function): Promise<void> => {
    const {
      tracking: {
        assets: {
          selectedAsset : {
            deviceEsn
          },
          smarterTrailer : {
            devicesHaveTrailerSensors,
            selectedAssetTrailer,
            sensorUpdateLoading
          }
        }
      }
    } = getState();
    if (!sensorUpdateLoading) {
      try {
        let sensorData = null;
        let sensorApps = hasSensorApps();
        dispatch({type: UPDATE_SMARTER_TRAILER_REQUEST});
        const deviceHasSmarterTrailer = devicesHaveTrailerSensors.find(
          ({esn: deviceHasSensorEsn}) => deviceHasSensorEsn === deviceEsn
        );
        if (deviceHasSmarterTrailer?.apps?.includes(SensorTypes.DoorState) ||
          sensorApps?.includes(SensorTypes.DoorState)) {
          sensorData = await getDoorSensorHistory(deviceEsn);
        }
        sensorApps = trimDoorStateFromApps(deviceHasSmarterTrailer?.apps);
        const latestData = await getLatestSensorData(
          deviceEsn,
          sensorApps.join(', ')
        );
        sensorData = sensorData ? sensorData.concat(latestData) : latestData;
        const augmentedData = augmentSensorData(
          selectedAssetTrailer,
          sensorData,
          isSameESN(deviceEsn, selectedAssetTrailer)
        );
        dispatch({type: UPDATE_SMARTER_TRAILER_RESPONSE, trailer: augmentedData});
      } catch (err) {
        dispatch({type: UPDATE_SMARTER_TRAILER_ERROR, err});
      }
    }
  };
};

/**
 *
 * To set the tracker so we can determine if another call needs to be made
 * to update the sensor data
 */
export const setSmarterTrailerSensorNeedsUpdate = (value) => ({
  type : SMARTER_TRAILER_SENSOR_NEEDS_UPDATE, value
});

/**
 * Function that gets the sensor data but only returns the most recent reading
 */
const getLatestSensorData = async(esn, apps: string) => {
  const latest = await getSensorData({
    mobileId : esn,
    apps
  });
  return latest;
};

/**
 * Function that gets door sensor history data
 */
const getDoorSensorHistory = async(esn) => {
  const history = await getSensorData({
    mobileId        : esn,
    apps            : SensorTypes.DoorState,
    numberOfRecords : 4
  });
  return history;
};

/**
 * @toggleMobileFilters
 */
export const toggleMobileFilters = (show: boolean) => (
  {type: TOGGLE_MOBILE_FILTERS, show}
);

// Checks for which sensor apps the user has and returns an array of
// the ones they do have.
const hasSensorApps = (): SensorTypes[] => {
  let apps = [];
  if (Features.hasSmartTrailer()) {
    apps = Object.values(SensorTypes);
  }
  return apps;
};

// If the passed array includes door state remove it because
// we don't want to make an extra call with it, it has it's own
// call for the 4 most recent
const trimDoorStateFromApps = (apps: SensorTypes[]) => {
  if (apps.includes(SensorTypes.DoorState)) {
    const index = apps.indexOf(SensorTypes.DoorState);
    apps.splice(index, 1);
  }
  return apps;
};

// This function compares the device's features with the user's approved
// apps to make sure we are only asking for what the device has enabled.
const getAvailableSensorApps = (userApps, features) => {
  const apps = userApps.filter(userApp => {
    return features[DeviceFeatureSensorTypes[userApp]] === EntityStatus.Enabled;
  });
  return apps;
};

/**
 * Defines a utility to validate if the same ESN
 */
const isSameESN = (deviceEsn: string, smarterTrailer: AugmentedSmarterTrailerSensorData) => {
  if (smarterTrailer?.rawData?.length) {
    const {mobileId} = smarterTrailer?.rawData[0]?.sensorData || {};
    return deviceEsn === mobileId;
  }
  return false;
};

/**
 *
 * Update Setting layers from User preferences
 */
export const updateLayers = ({
  enabledLayers,
  trafficSubLayers,
  trafficOpacity,
  weatherOpacity,
  geofenceOpacity,
  geofenceBorderOnly,
  geofenceLabelState,
  isClusteringActive,
  scDevicesLabelContent,
  isScDevicesLabelActive,
  labelContent
}, layers) => {
  return async dispatch => {
    const currentLayers = enabledLayers?.length ? layers.concat(enabledLayers) : layers || [];
    // [ION-APP TODO]: Need to revisit this implementation as there are far too many actions
    // being dispatched. It can be done with single action(may be new action)
    batch(() => {
      dispatch(setTrackingMapLayers(currentLayers));
      dispatch(setFleetLayerPersistenceActive(currentLayers?.includes(CURRENT_LAYER)));
      trafficSubLayers && dispatch(setTrafficLayerSubLayers(trafficSubLayers));
      !isNil(trafficOpacity) && dispatch(setTrafficLayerOpacity(trafficOpacity));
      !isNil(weatherOpacity) && dispatch(setWeatherLayerOpacity(weatherOpacity));
      !isNil(geofenceOpacity) && dispatch(setGeofenceLayerOpacity(geofenceOpacity));
      geofenceBorderOnly && dispatch(setGeofenceBorderOnly(geofenceBorderOnly));
      geofenceLabelState && dispatch(setGeofenceLabelState(geofenceLabelState));
      !isUndefined(isClusteringActive) && dispatch(toggleAssetClustering(isClusteringActive));
      labelContent && dispatch(onChangeAssetContent(labelContent));
      scDevicesLabelContent && dispatch(onChangeDeviceLabelContent(scDevicesLabelContent));
      !isNil(isScDevicesLabelActive) && dispatch(toggleDeviceLabelling(isScDevicesLabelActive));
    });
  };
};

/**
 * updateGeofenceComponents method updates the Geofence filter and Panel
 */
export const updateGeofenceComponents = (geoFenceFilterOpen: boolean, geoFencePanelOpen: boolean) => {
  return async dispatch => {
    batch(() => {
      dispatch(setGeoFenceFilterOpen(geoFenceFilterOpen));
      dispatch(setGeoFencePanelOpen(geoFencePanelOpen));
    });
  };
};

/**
 * setAssetFindNearby method update the Find Nearby Asset and Selected Asset
 */
export const setAssetFindNearby = (selectedAsset: CVSAssetType, asset: CVSAssetType) => {
  return async dispatch => {
    const assetAsDevices = selectedAsset
      ? Devices.getAssetsFromCollection([selectedAsset])[0]
      : null;
    dispatch(setSelectedAsset({asset: assetAsDevices, coordinates: true}) as any);
    dispatch(setFindNearbyAsset(asset));
  };
};

/**
 * setLocationPopupFindNearby method updates the location of the Find Nearby and selected asset
 */
export const setLocationPopupFindNearby = (locationCoordinates: any, selectedAsset: CVSAssetType = null) => {
  return async dispatch => {
    const assetAsDevices = selectedAsset
      ? Devices.getAssetsFromCollection([selectedAsset])[0]
      : null;
    dispatch(setLocationFindNearby(locationCoordinates));
    selectedAsset && dispatch(setSelectedAsset({asset: assetAsDevices}));
  };
};

/**
 * setFindNearbyForGeofence method updates Find Nearby for Geofence and selected asset
 */
export const setFindNearbyForGeofence = (geofence: any, selectedAsset: CVSAssetType = null) => {
  return async dispatch => {
    const assetAsDevices = selectedAsset
      ? Devices.getAssetsFromCollection([selectedAsset])[0]
      : null;
    batch(() => {
      dispatch(setFindNearbyGeofence(geofence));
      dispatch(setSelectedAsset({asset: assetAsDevices}));
    });
  };
};

/**
 * setHistoryUpdates method updates History Live and Layer List
 */
export const setHistoryUpdates = (layerListUpdate: boolean = false, historyLiveUpadate: boolean = false) => {
  return async dispatch => {
    batch(() => {
      dispatch(setIsLayerListUpdate(layerListUpdate));
      dispatch(setAssetsHistoryUpdatesLive(historyLiveUpadate));
      dispatch(showHistoryRefreshMessage(false));
      dispatch(resetAssetHistory());
    });
  };
};

export const clearSelectedFilters = () => ({type: CLEAR_SELECTED_FILTERS});

export const viewAssetHistory = ({selectedAsset, dateVal = undefined}) => {
  return dispatch => {
    dispatch(clearSelectedFilters());
    dispatch(setSelectedFiltersActive(true));
    dispatch(applyAssetHistoryDateFilter({
      fromDate      : startOfDay(dateVal),
      toDate        : endOfDay(dateVal),
      selectedRadio : dateVal ? DateSelectorValues.Custom : DateSelectorValues.Today
    }));
    const assetDevice = getItemValue(selectedAsset);
    // Create a mask to map the grid filter the event for the asset is selected
    // from map
    const deepLinkingAsset = {...assetDevice, isMapClick: !!dateVal};
    dispatch({type: SET_ASSETS_FILTER_RESPONSE, value: [deepLinkingAsset] || []});
    dispatch({type: EXIT_FIND_NEARBY});
  };
};

type onAssetSelectedProp = {
  asset: CVSAssetType;
  media: MediaProps;
  resetTool: boolean;
  isMapSelection?: boolean | null;
};

export const onAssetSelected = ({
  asset,
  media,
  resetTool = false,
  isMapSelection = null
}: onAssetSelectedProp) => {
  return dispatch => {
    const {isDesktop, isPhone} = media || {};
    // current theme: an SC device arrives already built as a Device - wrapped
    // again as an asset it lost its device id, and the details' API calls
    // went out with `undefined`. The legacy theme wraps it, as before ...
    const assetAsDevice = (isCurrentTheme() && asset instanceof Devices)
      ? asset
      : Devices.getAssetsFromCollection([asset])[0];
    dispatch(setSideSheetStatus({
      activeSideSheet      : getRoute(Route.TRACKING2),
      desktopSideSheetOpen : isDesktop
    }));
    isPhone && dispatch(setSideSheetView(SideSheetView.detailed));
    dispatch(setSelectedAsset({asset: assetAsDevice, resetTool, coordinates: isMapSelection}) as any);
    dispatch(setAssetDetailView({
      value            : true,
      forcePanelToOpen : true,
      fromTools        : false,
      selectedItem     : assetAsDevice
    }));
  };
};

/**
 * setGridFilteredAssetIds updates the list of assets (or more precisely,
 * asset IDs, to save memory) that result from the application of filters
 * in the Table View grid. Assets passed to the Tracking map can then be
 * filtered against this list before being displayed on the map.
 * If the device is SC Device, then use id, else use assetId.
 * Determined using isScDevice.
 * If the list is:
 *   null      => include all assets
 *   empty     => include no assets
 *   non-empty => include only assets in the list
 */
export const setGridFilteredAssetIds = (assets: CVSAssetType[]) => {
  return dispatch => {
    dispatch({
      type     : SET_GRID_FILTERED_ASSET_IDS,
      assetIds : assets ? assets.map(({assetId, id, isScDevice = undefined}) =>
        isScDevice ? id : assetId) : null
    });
  };
};

/**
 * Defines a utility to get Cargo Params.
 */
const getCargoParams = ({
  getState,
  selectedAssets,
  selectedDevices,
  globalGroupHierarchy
}: CargoParamsProps) => {
  const groupIds       = getCheckedAccountIds(getState);
  const assets         = getFilteredAssets(selectedAssets, groupIds);
  const assetsAllowed  = getItemAllowed(assets, globalGroupHierarchy);
  const assetIds       = assetsAllowed?.map(asset => asset?.id) || [];
  const devicesAllowed = getItemAllowed(selectedDevices, globalGroupHierarchy);
  const deviceIds      = devicesAllowed?.map(device => device?.id);

  return {
    groups  : groupIds,
    assets  : assetIds,
    devices : deviceIds
  };
};

/**
 * Defines a utility to get the SC Devices data based on the some parameters
 */
export const loadSCDevices = () => {
  return async(dispatch, getState) => {
    if (Features.hasSCIAccess()) {
      const {
        tracking: {
          filters: {
            devices : devicesFilters,
            assets : {
              selected : selectedAssets
            }
          }
        },
        globalGroup: {
          globalGroupHierarchy
        }
      } = getState();
      const {
        selected: selectedDevices
      } = devicesFilters || [];
      try {
        const params = getCargoParams({
          getState,
          selectedAssets,
          selectedDevices,
          globalGroupHierarchy
        });

        dispatch({type: LOAD_SC_DEVICES_REQUEST});
        const scDevices = await getSCDevices(params);
        dispatch({type: LOAD_SC_DEVICES_RESPONSE, scDevices: parseSciCollection(scDevices)});
      } catch (error) {
        dispatch({type: LOAD_SC_DEVICES_ERROR, error});
      }
    }
  };
};

/**
 * Defines a utility to get the SC Devices data updates based on the some parameters
 */
export const loadSCDevicesUpdates = (dbWriteTime: string) => {
  return async(dispatch, getState) => {
    try {
      let latestTime = null;
      const {
        tracking: {
          assets: {
            selectedAsset
          },
          filters: {
            devices : devicesFilters,
            assets : {
              selected : selectedAssets
            }
          },
          fleetMode: {
            isLargeFleetMode
          },
          supplyChainDevices: {
            devices : currentDevices,
            updates : currentDevicesUpdates
          },
          grid: {
            filteredAssetIds
          },
          findNearby: {
            isActive
          }
        },
        globalGroup: {
          globalGroupHierarchy
        }
      } = getState();

      if (filteredAssetIds?.length || isActive) {
        const allDevices = [...currentDevices, ...currentDevicesUpdates];
        const dates = allDevices?.map((device) => {
          const value = device[DATETIME_PROPERTY];
          if (value) {
            return new Date(value)?.getTime();
          }
          return null;
        });
        latestTime = dates?.length
          ? new Date(Math.max(...dates)).toISOString()
          : null;
      }

      const where = {
        time : latestTime || dbWriteTime
      };

      const {
        selected: selectedDevices
      } = devicesFilters || [];
      const groupIds       = getCheckedAccountIds(getState);
      const assets         = getFilteredAssets(selectedAssets, groupIds);
      const assetsAllowed  = getItemAllowed(assets, globalGroupHierarchy);
      const assetIds       = assetsAllowed?.map(asset => asset?.id) || [];
      const devicesAllowed = getItemAllowed(selectedDevices, globalGroupHierarchy);
      const deviceIds      = devicesAllowed?.map(({id}) => id);
      const params = {
        groups  : groupIds,
        assets  : assetIds,
        devices : deviceIds,
        where
      };
      const scDevicesUpdates = await getSCDevices(params);
      const {features} = scDevicesUpdates?.data || {};
      if (features?.length) {
        dispatch({type: LOAD_SC_DEVICES_UPDATES, scDevicesUpdates: parseSciCollection(scDevicesUpdates)});
        if (!isLargeFleetMode && selectedAsset) {
          const devicesUpdated = features.find(({attributes}) =>
            attributes?.device_id === selectedAsset?.id());
          if (devicesUpdated) {
            dispatch({
              type           : SET_SELECTED_ASSET,
              asset          : Devices.getDevicesFromCollection([devicesUpdated])[0],
              isMapSelection : false
            });
          }
        }
      }
    } catch (err) {
      error(err);
    }
  };
};

/**
 * Defines a utility to handle the opacity of the SC Device Layer
 */
export const setSCDeviceLayerOpacity = (opacity) => {
  return {type: SET_SC_DEVICES_OPACITY, opacity};
};

/**
 * Defines a utility to reset SC Device state
 */
export const resetSCDevices = () => {
  return {type: RESET_SC_DEVICES};
};

// invoked by IonGrid when the grid filter model changed...
export const setFilterModel = (model: FilterModel) => ({
  type : SET_TRACKING_GRID_FILTER_MODEL, model
});

export const setGridState = (state: GenericObject, reset = false) => ({
  type : SET_TRACKING_GRID_STATE, state : {...state, reset}
});

export const setFleetUpdateFlag = (state: boolean) => ({
  type : SET_FLEET_UPDATE_FLAG, state
});

export const setFleetUpdateResetFlag = (state: boolean) => ({
  type : SET_FLEET_UPDATE_RESET_FLAG, state
});

// registers/unregisters a tracking2 fleet-update consumer (grid, map layers,
// sidesheet list) as currently fetching, keyed by a stable literal id - lets
// AssetUpdateProgressBar's own cycle wait until every consumer that reacted
// to its trigger has actually settled, not just assume a fixed duration ...
export const setFleetUpdateFetching = (key: string, isFetching: boolean) => ({
  type : SET_FLEET_UPDATE_FETCHING, key, isFetching
});

export const setLargeFleetGridCount = (count) => ({
  type : SET_LARGE_FLEET_GRID_COUNT, count
});

export const setSavedMapViews = (mapViews: SavedMapViewType[]) => ({
  type : SET_MAP_VIEWS, mapViewsToSave : mapViews
});

export const setRouteMatchingEnabled = (state: boolean) => ({
  type : SET_ROUTE_MATCHING_ENABLED,
  state
});

export const fetchEditableDevice = (device: Devices) => {
  return async(dispatch, getState) => {
    const deviceId = device?.getDevice?.()?.deviceId ?? null;
    const accountId = device?.getDevice?.()?.accountId ?? null;
    const {
      globalGroup : {
        currentAccount
      }
    } = getState();
    const globalGroupId = entityId(currentAccount);
    if (!isNil(deviceId)) {
      const ctcDevice = await loadDevicesBySmartSearch({
        accountId             : globalGroupId,
        pageId                : 1,
        pageSize              : 1,
        queryString           : `(id:${deviceId})`,
        includeSubGroups      : true,
        includeDeviceSettings : true,
        includeAccountFilter  : true
      });
      dispatch({type: SET_EDITABLE_DEVICE, device: ctcDevice?.devices?.[0]});
    }
  };
};

/**
 * Defines a utility to set History Report Status
 */
const setHistoryReportStatus = () => {
  return async(dispatch, getState) => {
    const {
      tracking: {
        maps : {
          layers
        }
      }
    } = getState();
    if (layers?.includes(HISTORY_LAYER)) {
      dispatch(showHistoryRefreshMessage(true));
    }
    if (layers?.includes(CURRENT_LAYER)) {
      dispatch(resetAssetHistory());
    }
    dispatch(setAssetsHistoryUpdatesLive(false));
  };
};

const validateLargeFleetMode = ({
  largeFleetModeState,
  response,
  params,
  selectedDevices = []
}) => {
  return async(dispatch, getState) => {
    const {
      nav: {
        currentPage
      }
    } = getState();
    const totalCount = (response?.count ?? 0) + selectedDevices.length;
    const largeFleetMode = getIsLargeFleetMode(totalCount);
    if (!isCurrentPage(currentPage, Route.TRACKING2)) {
      dispatch(setAssetsCount(largeFleetMode, totalCount));
      dispatch({type: SET_LARGE_FLEET_STATE, largeFleetMode, response, refreshMap: true});
      if (!largeFleetMode) {
        const assets = await getCVSAssets(params);
        dispatch({type: INITIALIZE_TRACKING, assets: Devices.getAssetsFromCollection(assets)});
      }
      dispatch(updateLargeFleetAssetList(largeFleetModeState, largeFleetMode, params));
    }
  };
};

/**
 * Action to set asset directly to the store
 */
export const setAssetSelected = (asset) => {
  return (dispatch) => dispatch({
    type : SET_SELECTED_ASSET,
    asset
  });
};

export const refreshTrackingPage = () => {
  return (dispatch) => {
    dispatch({type: RESET_TRACKING});
    dispatch({type: RESET_SC_DEVICES});
  };
};

export const setTrackingMode = (trackingMode = TrackingMode.Live) => {
  return (dispatch) => {
    if (trackingMode === TrackingMode.Live) {
      dispatch(applyAssetHistoryDateFilter({
        selectedRadio : DateSelectorValues.Live,
        fromDate      : null,
        endDate       : null
      }) as any);
      dispatch(clearQueryParams());
      setQuery();
      changePage(Route.TRACKING2, '/', false);
    } else {
      dispatch(initializeReports());
    }
    dispatch({type: SET_TRACKING_MODE, trackingMode});
  };
};

export const setSelectedFiltersActive = (isActive: boolean) => {
  return dispatch => {
    dispatch({type: SET_SELECTED_FILTERS_ACTIVE, isActive});
  };
};

export const setGridFilteredAssets = (assets: Device[], devices: Device[]) => ({
  type : SET_TRACKING_GRID_FILTERED_ASSETS, assets, devices
});

// action to launch SHR from anywhere from the app...
export const launchSHR = ({
  selectedDevice,
  date,
  redirectPage = true
}: LaunchSHRProps) => {
  return async(dispatch) => {
    dispatch(clearFilters(ReportsMode.ShipmentHistory));
    selectedDevice && dispatch({
      type       : APPLY_DEVICES_FILTER,
      reportType : ReportsMode.ShipmentHistory,
      selected   : [selectedDevice]
    });
    if (date) {
      dispatch({
        type       : DATE_FILTER_REQUEST,
        reportType : ReportsMode.ShipmentHistory,
        date       : {
          startDate : startOfDay(date),
          endDate   : endOfDay(date),
          dateValue : DateSelectorValues.Custom
        }
      });
    }
    redirectPage && changePage(Route.REPORTS, '/', false);
    dispatch({type: EXIT_FIND_NEARBY});
  };
};
